import { favIndex, saveFavorite, loadFavorite, removeFavorite } from './favorites.js';
import {
  checkAuth,
  accountBilling,
  logout,
  renderBatch,
  isInsufficientFunds,
  hasKnownEmptyBalance,
  normalizeSubject,
  estimateRollCost,
  paymentMethod,
  setPaymentMethod,
  paymentToken,
  describeTileForUpscale,
  renderUpscale,
  renderTileAnimation,
  estimateAnimationCost,
  ANIMATION_ENGINES
} from './sogni.js';
import {
  showVideoWall,
  hideVideoWall,
  isVideoWallActive,
  isVideoWallMuted,
  setVideoWallMuted
} from './videowall.js';
import { accountDisplay } from './account.js';
import { enterPreview, exitPreview } from './preview.js';
import { setBackground, currentBackground, onBackgroundChange } from './background.js';
import { openPromptModal, setPromptConfig, onPromptResubmit } from './promptmodal.js';
import {
  publishEnabled,
  setPublishEnabled,
  publishTile,
  publishKeeperTile,
  fetchStream,
  toggleStreamFavorite,
  fetchWhoami,
  deleteStreamEntry,
  unflagStreamEntry
} from './stream.js';
import { openReportModal, setReportConfig } from './reportmodal.js';
import { addActionMenu, closeActionMenu } from './actionmenu.js';
// Tile size lives in tilesize.js, shared with the fullscreen wheel-zoom and
// the zoom rail's buttons.
import { getTileSize, setMaxTileSize, zoomCentered } from './tilesize.js';
import { startUpdateWatch } from './update.js';
import { initSettings, selectedModel, allowMature, selectedFormat } from './settings.js';
import { initSparkModal, openSparkModal } from './sparkmodal.js';
import {
  clearMonthlyBoost,
  initMonthlyBoost,
  openMonthlyBoost,
  refreshMonthlyBoost
} from './monthlyboost.js';
import { initAuthModal, openAuthModal } from './authmodal.js';
import { consumeCanvasDragClick } from './canvas.js';
import { downloadTile } from './download.js';

const el = (id) => document.getElementById(id);

// One image per preview. A real <img> (not a CSS background) so it can still
// be right-clicked and saved.
// Every src today is ours (a server-minted tile path, a blob URL, a literal
// asset), but this builds markup from a string, so it escapes the attribute
// rather than depending on that staying true.
const attr = (value) =>
  String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
const tiled = (src) => `<img src="${attr(src)}" alt="" loading="lazy">`;

// One icon set, defined once in the markup. Emoji used to carry the whole
// interface, but ⛶, ⧉ and ⤓ render inconsistently or not at all across
// platforms. Subject labels keep their emoji: there it is content.
const icon = (name, size = 14) =>
  `<svg width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true"><use href="#i-${name}"/></svg>`;

/** The full-bleed button that selects a tile. Layered under the tile's own
 *  controls rather than wrapping them. */
function addOpen(node, label, onClick) {
  const open = document.createElement('button');
  open.type = 'button';
  open.className = 'tile-open';
  open.setAttribute('aria-label', label);
  open.onclick = onClick;
  node.appendChild(open);
  return open;
}

/** The control cluster on a tile, created on first use so download and
 *  remove share one row rather than being scattered into opposite corners.
 *  Revealed on hover for a mouse; CSS pins it open for touch and keyboard,
 *  where these controls used to be permanently unreachable. */
function tileActions(node) {
  let box = node.querySelector('.tile-actions');
  if (!box) {
    box = document.createElement('span');
    box.className = 'tile-actions';
    node.appendChild(box);
  }
  return box;
}

/** The two actions every tile has, wherever it lives. Menu items rather than
 *  icon buttons: see actionmenu.js for why the icon row had to go. */
function commonTileItems({ src, name, seed, entry }) {
  return [
    {
      label: 'Download',
      icon: 'download',
      onClick: () => downloadTile(src, name, seed)
    },
    // Legacy entries carry no prompt provenance; they simply omit the item.
    entry?.prompt && {
      label: 'View prompt',
      icon: 'prompt',
      onClick: () => openPromptModal(entry)
    }
  ];
}

const goBtn = el('go');
const statusEl = el('status');
const slotsEl = el('slots');
const slotHint = el('slot-hint');
const winnerEl = el('winner');
const winnerLabel = el('winner-label');
const favBtn = el('fav');
const favsEl = el('favs');
const loginEl = el('login');
const accountEl = el('account');

let config = null;
let subjects = [];
let styles = [];
let current = null; // last generated winner (blob-backed)
let batchTiles = []; // scored tiles of the running/last batch
let activeRoll = null;
const objectUrls = new Map();

// ---------- auth ----------
async function refreshAccountChip(username) {
  el('account-user').textContent = username || '';
  accountEl.classList.remove('hidden');
  const billing = await accountBilling();
  const display = accountDisplay(billing);
  lastDisplay = display;
  el('unlimited-link').classList.toggle('hidden', display.unlimited);
  // Buying Spark and the per-spin cost only matter for token-billed accounts:
  // an Unlimited entitlement covers every render here, so there is nothing to
  // top up and nothing to spend.
  isUnlimited = display.unlimited;
  el('buy-spark').classList.toggle('hidden', display.unlimited);
  paintChip();
  applyPaymentUI(display);
  refreshCostHint();
  return billing;
}

// ---------- payment method ----------
// The account menu lets a signed-in visitor bill renders to their Unlimited
// plan (when active), Spark, or SOGNI. The plan option only appears with an
// active plan; without one, a stored 'plan' choice reads as Spark, which is
// what auto billing spends anyway.
const PAY_OPTS = ['plan', 'spark', 'sogni'];
// The last billing snapshot, kept so the chip and menu can repaint on a method
// switch without another balance round trip.
let lastDisplay = null;

function activePaymentMethod() {
  const method = paymentMethod();
  return method === 'plan' && !isUnlimited ? 'spark' : method;
}

// The account chip mirrors the active method: the plan shows its badge and no
// number (an Unlimited render costs nothing), Spark shows a bolt and its count,
// SOGNI shows its balance named in words.
function paintChip() {
  const balEl = el('account-spark');
  const badge = el('unlimited-badge');
  const method = activePaymentMethod();
  const d = lastDisplay;
  if (method === 'plan') {
    balEl.textContent = '';
    balEl.removeAttribute('data-token');
    badge.classList.remove('hidden');
    return;
  }
  badge.classList.add('hidden');
  if (method === 'sogni') {
    balEl.dataset.token = 'sogni';
    balEl.textContent =
      d?.sogni != null
        ? `${d.sogni.toLocaleString(undefined, { maximumFractionDigits: 2 })} SOGNI`
        : '';
  } else {
    balEl.dataset.token = 'spark';
    balEl.textContent = d?.spark != null ? d.spark.toLocaleString() : '';
  }
}

function applyPaymentUI(display) {
  // The plan option only exists with an active plan. A stored 'plan' choice on
  // an account without one is not rewritten — it bills as auto+spark (exactly
  // the prior default) and quietly starts using a plan the moment one appears —
  // it just shows as Spark, via activePaymentMethod.
  el('pay-plan').classList.toggle('hidden', !display.unlimited);
  el('pay-spark-bal').textContent = display.spark != null ? display.spark.toLocaleString() : '';
  el('pay-sogni-bal').textContent =
    display.sogni != null
      ? display.sogni.toLocaleString(undefined, { maximumFractionDigits: 2 })
      : '';
  const active = activePaymentMethod();
  for (const method of PAY_OPTS) {
    el(`pay-${method}`).setAttribute('aria-checked', String(method === active));
  }
}

for (const method of PAY_OPTS) {
  el(`pay-${method}`).onclick = (e) => {
    // Keep the menu open so the choice and its check stay visible; the document
    // click-to-close handler would otherwise dismiss it on this very click.
    e.stopPropagation();
    setPaymentMethod(method);
    // Repaint the menu, the chip and the cost hint from the last known balances,
    // without another network round trip.
    const active = activePaymentMethod();
    for (const m of PAY_OPTS) el(`pay-${m}`).setAttribute('aria-checked', String(m === active));
    paintChip();
    refreshCostHint();
  };
}

// A small per-spin cost estimate by the Tessellate button, so a token-billed
// user sees roughly what a roll costs before pressing. The number is the
// platform's own estimate for one batch; the real charge always shows on the
// balance chip afterward.
let costHintSeq = 0;
let lastCostKey = '';

async function estimateCurrentRollCost(tokenType) {
  const photoMode = el('subject-select').value === 'photo' && !!config.photo;
  const photoStyle = el('style-select').value;
  const photoContextImages = photoStyle === 'random' || config?.photo?.donors?.[photoStyle] ? 2 : 1;
  const { model, sampler, scheduler } = photoMode
    ? {
        model: config.photo.model,
        sampler: config.photo.sampler,
        scheduler: config.photo.scheduler
      }
    : selectedModel();
  return estimateRollCost({
    model,
    imageCount: config.batchSize,
    size: config.size,
    sampler,
    scheduler,
    ...(photoMode
      ? {
          steps: config.photo.steps,
          guidance: config.photo.guidance,
          contextImages: photoContextImages
        }
      : {}),
    tokenType
  });
}

async function refreshCostHint() {
  const hintEl = el('cost-hint');
  const method = activePaymentMethod();
  // A render only costs the visitor when it is not covered by an active plan:
  // that is the plan method on an Unlimited account. Spark and SOGNI always
  // spend, plan or no plan. Signed-out visitors cannot roll at all.
  const covered = isUnlimited && method === 'plan';
  if (!isAuthed || covered || !config) {
    hintEl.classList.add('hidden');
    lastCostKey = '';
    return;
  }
  const token = paymentToken(method);
  // Photo mode renders on the identity-edit model with fixed knobs and a
  // context image, so its estimate differs from the picker model's. The
  // config.photo check covers a cached pre-photo /api/config just after a
  // deploy — the picker model's estimate is then simply shown instead.
  const photoMode = el('subject-select').value === 'photo' && !!config.photo;
  const { model, sampler, scheduler } = photoMode
    ? {
        model: config.photo.model,
        sampler: config.photo.sampler,
        scheduler: config.photo.scheduler
      }
    : selectedModel();
  // The estimate depends only on model, knobs, batch size, tile size and which
  // token pays — none of which change mid-roll — so skip the network call when
  // nothing that affects the figure has moved and one is already on screen.
  const photoStyle = photoMode ? el('style-select').value : '';
  const key = [
    model,
    sampler,
    scheduler,
    config.batchSize,
    config.size,
    token,
    photoMode,
    photoStyle
  ].join('|');
  if (key === lastCostKey && !hintEl.classList.contains('hidden')) return;
  const seq = ++costHintSeq;
  try {
    const cost = await estimateCurrentRollCost(token);
    if (seq !== costHintSeq) return; // a newer request superseded this one
    const amount = cost?.[token];
    if (amount == null) {
      hintEl.classList.add('hidden');
      lastCostKey = '';
      return;
    }
    const usd = cost.usd != null ? ` · ~$${cost.usd.toFixed(2)}` : '';
    // Spark is a whole-token count with a bolt; SOGNI is a small, often
    // fractional amount, named in words.
    const label =
      token === 'spark'
        ? `${icon('bolt', 11)} ${Math.max(0, Math.round(amount)).toLocaleString()} per spin`
        : `${(Math.round(amount * 1000) / 1000).toLocaleString(undefined, { maximumFractionDigits: 3 })} SOGNI per spin`;
    hintEl.innerHTML = `${label}${usd}`;
    hintEl.classList.remove('hidden');
    lastCostKey = key;
  } catch {
    if (seq === costHintSeq) {
      hintEl.classList.add('hidden');
      lastCostKey = '';
    }
  }
}
// Model or advanced-knob changes alter the estimate; recompute when they fire.
window.addEventListener('sogni-settings-changed', refreshCostHint);

// Rendering uses the signed-in user's Unlimited entitlement when available,
// otherwise their own Spark, so it is gated on a verified session. Signed-out
// visitors get everything except making new tiles.
let isAuthed = false;
// Who the server sees us as, and whether it will let us moderate. Both come
// from the server (never asserted here) and drive only which controls show —
// every delete is authorized again server-side.
let currentUsername = null;
let canModerate = false;
// Set from the billing lookup: Unlimited accounts spend nothing per roll, so
// the cost hint and Buy-Spark controls stay hidden for them.
let isUnlimited = false;

async function initAuth() {
  const { authed, username } = await checkAuth();
  isAuthed = authed;
  loginEl.classList.toggle('hidden', authed);
  goBtn.classList.toggle('hidden', !authed);
  el('picker').classList.toggle('hidden', !authed);
  // The drawer holds one collection you can act on: your keepers once you can
  // make tiles, the stock samples until then. Both sit beside the stream.
  // Signed out there is nothing in the dock but a hidden picker, so the whole
  // console panel goes rather than sitting there as an empty bar.
  el('dock').classList.toggle('hidden', !authed);
  el('tab-samples').classList.toggle('hidden', authed);
  el('tab-keepers').classList.toggle('hidden', !authed);
  // The spine mirrors the tab strip: one collection icon per session — keepers
  // once you can make tiles, the stock samples until then.
  el('spine-samples').classList.toggle('hidden', authed);
  el('spine-keepers').classList.toggle('hidden', !authed);
  if (authed) {
    statusEl.textContent = 'Roll the dice until the pattern tiles perfectly.';
    const billing = await refreshAccountChip(username);
    await refreshMonthlyBoost(billing.subscription?.status);
    currentUsername = username || null;
    // Moderator status is the server's to confirm; fetch it once so the right
    // controls appear. A failure just hides an affordance, never grants one.
    try {
      const who = await fetchWhoami();
      currentUsername = who.username || currentUsername;
      canModerate = !!who.canModerate;
    } catch {
      canModerate = false;
    }
  } else {
    accountEl.classList.add('hidden');
    statusEl.textContent = '';
    currentUsername = null;
    canModerate = false;
    isUnlimited = false;
    el('cost-hint').classList.add('hidden');
    clearMonthlyBoost();
  }
  // Upscaling and animating render on the visitor's account like everything
  // else, so both rail buttons appear and disappear with the session.
  updateUpscaleUI();
  updateAnimateUI();
  // Whichever of samples/keepers this session cannot use hands over to the
  // other. Re-selecting also refreshes the stream, whose delete controls
  // depend on who we now are (e.g. after signing in on another tab).
  const unavailable = authed ? 'samples' : 'keepers';
  selectTab(activeTab === unavailable ? (authed ? 'keepers' : 'samples') : activeTab);
  // Keep the spine's active section in step with what this session can use, so
  // an open studio never points at a collection that is no longer there.
  if (deckSection === unavailable) deckSection = authed ? 'keepers' : 'samples';
  paintSpine();
  return authed;
}

el('login-link').onclick = () => openAuthModal('login');
el('signup-link').onclick = () => openAuthModal('signup');
window.addEventListener('focus', () => {
  if (!loginEl.classList.contains('hidden')) initAuth();
});
el('logout').onclick = async () => {
  await logout();
  initAuth();
};

// ---------- account menu ----------
// Balance, top-up, Unlimited, settings and sign-out used to sit side by side
// in one 30px strip, each with its own visual treatment. They are one control
// now: a chip that says who you are and what you have, and a menu behind it.
const accountChip = el('account-chip');
const accountMenu = el('account-menu');

function closeAccountMenu() {
  accountMenu.classList.add('hidden');
  accountChip.setAttribute('aria-expanded', 'false');
}

accountChip.onclick = (e) => {
  e.stopPropagation();
  const opening = accountMenu.classList.contains('hidden');
  accountMenu.classList.toggle('hidden', !opening);
  accountChip.setAttribute('aria-expanded', String(opening));
};
accountMenu.onclick = (e) => {
  if (e.target.closest('.menu-item')) closeAccountMenu();
};
document.addEventListener('click', closeAccountMenu);
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape' || accountMenu.classList.contains('hidden')) return;
  closeAccountMenu();
  accountChip.focus();
});

// ---------- info tips ----------
// Hover/focus reveals a tip via CSS alone; touch has neither, so a tap
// toggles .open, and tapping anywhere else (or Escape) closes it again.
document.addEventListener('click', (e) => {
  const tip = e.target.closest('.info-tip');
  for (const open of document.querySelectorAll('.info-tip.open')) {
    if (open !== tip) open.classList.remove('open');
  }
  tip?.classList.toggle('open');
});
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  for (const open of document.querySelectorAll('.info-tip.open')) open.classList.remove('open');
});

// ---------- upsell ----------
const upsellEl = el('upsell');
el('upsell-close').onclick = () => upsellEl.classList.add('hidden');
function showUpsell() {
  upsellEl.classList.remove('hidden');
}

// ---------- buy spark ----------
// Two entry points into the same modal: the always-present chip button, and
// the out-of-Spark dialog when a roll runs the balance dry.
el('buy-spark').onclick = () => openSparkModal();
el('upsell-buy').onclick = () => {
  upsellEl.classList.add('hidden');
  openSparkModal('You are out of Spark — top up to keep rolling.');
};
// A completed purchase credits Spark on the platform; reflect it in the chip
// without making the buyer reload. The chip reads the account it belongs to.
window.addEventListener('spark-purchased', () => {
  if (isAuthed) refreshAccountChip(currentUsername);
});

// ---------- slots ----------
function slot(i) {
  return slotsEl.querySelector(`[data-slot="${i}"]`);
}

function resetSlots() {
  // Every container rebuild has to take any open tile menu with it: the menu
  // lives on <body>, so clearing the grid leaves it floating over the NEW
  // tiles while still acting on the old ones.
  closeActionMenu();
  el('results').classList.remove('hidden');
  batchTiles = [];
  slotsEl.innerHTML = '';
  for (let i = 0; i < config.batchSize; i++) {
    // A button, not a div: these are the only way to inspect a render, and a
    // div with an onclick cannot be reached or fired from a keyboard.
    const s = document.createElement('div');
    s.className = 'slot rendering';
    s.dataset.slot = i;
    slotsEl.appendChild(s);
  }
}

// The first-run pitch has done its job the moment there is a tile on screen
// that the visitor chose or rolled. It fades; nothing moves.
function enterWorkingPhase() {
  document.body.dataset.phase = 'working';
}

// Keep is one button in three states rather than three strings assigned from
// four places.
function setKeepButton(state) {
  const label = { keep: 'Keep', saved: 'Kept', shared: 'Kept & shared' }[state];
  favBtn.innerHTML = `${icon('heart')} ${label}`;
  favBtn.classList.toggle('saved', state !== 'keep');
}

async function adoptTile(tile) {
  const blob = await fetch(tile.url)
    .then((r) => r.blob())
    .catch(() => null);
  const src = blob ? URL.createObjectURL(blob) : tile.url;
  setBackground(src);
  enterWorkingPhase();
  winnerEl.classList.remove('hidden');
  winnerLabel.textContent = `${tile.emoji || '✨'} ${tile.label}`;
  setKeepButton('keep');
  if (blob) {
    current = {
      id: `fav-${Date.now()}`,
      blob,
      src,
      label: tile.label,
      emoji: tile.emoji || '✨',
      seed: tile.seed,
      prompt: tile.prompt,
      model: tile.model,
      remoteUrl: tile.url,
      // Same provenance the auto-winner keeps. Without it, hand-picking a tile
      // from the batch produced a keeper that could not be reported and could
      // not be rerolled onto its own donor, while the winner beside it could.
      jobId: tile.jobId,
      projectId: tile.projectId,
      ...(tile.photoStyle ? { photoStyle: tile.photoStyle } : {}),
      favorited: false
    };
  }
}

// Deliberately hedged wording. The checker measures pixel continuity across
// the wrap, which catches tonal steps and misaligned shapes but is blind to a
// pattern that terminates cleanly at the border — pixels match while the forms
// are truncated, giving a band of half-motifs when tiled. Only a human sees
// that, so the badge reports a check, not a guarantee, and the raw ratio moves
// to the tooltip: it is a weak signal and it was never a number a visitor
// could act on. Clicking the tile fills the screen with it, which is where the
// question actually gets settled.
const VERDICT_LABEL = {
  pass: 'Seamless',
  near: 'Close',
  fail: 'Seam',
  trivial: 'Seam, sparse edges',
  // The identity edit returned the donor unchanged, so the visitor's face never
  // made it in. Named for what the visitor cares about, not for the mechanism.
  unchanged: 'Photo not applied',
  error: 'Failed'
};

function markSlot(i, tile) {
  const s = slot(i);
  s.className = `slot ${tile.verdict}`;
  if (tile.url) {
    const badge = VERDICT_LABEL[tile.verdict] || tile.verdict;
    const ratio = Math.max(tile.x, tile.y).toFixed(1);
    s.title = `Seam ratio ${ratio}. Click to fill the screen and judge it yourself.`;
    s.innerHTML = tiled(tile.url) + `<span class="badge">${badge}</span>`;
    addOpen(s, `${tile.label}: ${badge}, seam ratio ${ratio}`, () => {
      const shown = batchTiles.filter((t) => t.url);
      const list = shown.map((t) => ({
        src: t.url,
        label: `${t.emoji || ''} ${t.label} · seam ${Math.max(t.x, t.y).toFixed(1)}`.trim(),
        name: `${t.emoji || ''} ${t.label}`.trim(),
        prompt: t.prompt,
        seed: t.seed,
        model: t.model,
        // Carried so "Render this prompt" from fullscreen reaches for the same
        // donor. Without it the reroll paired a style's prompt with the
        // default style's topology.
        photoStyle: t.photoStyle,
        report: t.jobId
          ? {
              kind: 'generation',
              projectId: t.projectId,
              jobId: t.jobId,
              url: t.url,
              prompt: t.prompt,
              model: t.model,
              seed: t.seed
            }
          : null
      }));
      selectFromCollection(
        list,
        shown.findIndex((t) => t.url === tile.url)
      );
      adoptTile(tile);
    });
    addActionMenu(tileActions(s), () => [
      ...commonTileItems({ src: tile.url, name: tile.label, seed: tile.seed, entry: tile }),
      // A freshly rendered slot is a generation on the visitor's own account:
      // the platform owns that content, so the report goes there, naming the
      // job the ids were captured from.
      tile.jobId && {
        label: 'Report…',
        icon: 'flag',
        onClick: () =>
          openReportModal({
            kind: 'generation',
            name: tile.label,
            projectId: tile.projectId,
            jobId: tile.jobId,
            url: tile.url,
            prompt: tile.prompt,
            model: tile.model,
            seed: tile.seed
          })
      }
    ]);
  } else {
    s.title = 'This render did not come back.';
    s.innerHTML = `<span class="badge">${VERDICT_LABEL.error}</span>`;
  }
}

function failPendingSlots() {
  for (let i = 0; i < config.batchSize; i++) {
    if (slot(i)?.classList.contains('rendering')) markSlot(i, { verdict: 'error' });
  }
}

// ---------- generation ----------
function randomSeed() {
  return Math.floor(Math.random() * 2 ** 31);
}

async function scoreUrl(url, signal) {
  const res = await fetch('/api/score', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
    signal
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'scoring failed');
  return res.json();
}

// ---------- subject picker ----------
const selectEl = el('subject-select');
const styleEl = el('style-select');
const customRow = el('custom-row');
const customInput = el('custom-input');
const customEcho = el('custom-echo');
const customStyleRow = el('custom-style-row');
const customStyleInput = el('custom-style-input');
const styleField = el('style-field');
const rawRow = el('raw-row');
const rawInput = el('raw-input');
const photoRow = el('photo-row');
const photoInput = el('photo-input');
const photoChoose = el('photo-choose');
const photoChooseLabel = el('photo-choose-label');
const photoThumb = el('photo-thumb');
const photoClear = el('photo-clear');
const photoEcho = el('photo-echo');
const CHOICE_KEY = 'sogni-infinity-subject';
const STYLE_KEY = 'sogni-infinity-style';

// ---------- photo mode ----------
// The chosen photo, downscaled in this tab and held only in memory: it is
// deliberately never persisted, and it is sent only to the Sogni renderer as
// Reference B for the identity model — never to the Infinity server.
let photoBlob = null;
// One donor per photo style, fetched once and kept: the style the prompt route
// picked decides which topology goes in as Reference A, so a lacquer prompt can
// never be handed the risograph field.
const photoDonorPromises = new Map();

async function photoDonor(styleId) {
  const donors = config?.photo?.donors;
  // Frame recipes use the uploaded photo as their only reference. Only a
  // missing legacy style id falls back to the first donor.
  if (styleId && !donors?.[styleId]) return null;
  // A stored photo prompt that predates per-style donors names no style. Keep
  // that legacy case working by falling back to the first configured donor.
  const url = donors?.[styleId] || Object.values(donors || {})[0];
  if (!url) throw new Error('Photo topology is warming up — try again shortly.');
  if (!photoDonorPromises.has(url)) {
    photoDonorPromises.set(
      url,
      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error('Could not load the photo topology.');
          return response.blob();
        })
        .catch((error) => {
          photoDonorPromises.delete(url);
          throw error;
        })
    );
  }
  return photoDonorPromises.get(url);
}

/**
 * How far a rendered tile moved from the donor it was edited from: mean
 * absolute difference over a 64px greyscale, the same measure the research
 * script used to pick the threshold.
 *
 * The identity edit sometimes hands back the donor essentially untouched. That
 * is the one failure the seam gate cannot catch — an unedited donor is a
 * genuinely seamless tile, so it passes cleanly and, being the most seamless
 * thing in the batch, tends to be picked as the winner. Photo mode was
 * therefore selecting for its own failure, which is what "the likeness is not
 * being transferred" looks like from the outside.
 */
const DONOR_PROBE = 64;
// Matches CONFIG.photo.minDonorDistance, and used when the config in hand does
// not carry one. /api/config is cached for five minutes independently of the
// content-hashed bundle, so right after a deploy a browser can hold new JS and
// a config from before this field existed. Reading `undefined` there would
// make every comparison false and turn the guard off silently — the failure
// this whole check exists to prevent, restored without a single symptom.
const MIN_DONOR_DISTANCE = 2.6;

async function donorDistance(tileUrl, donorBlob) {
  const draw = async (src) => {
    const bitmap = await createImageBitmap(src);
    const canvas = document.createElement('canvas');
    canvas.width = DONOR_PROBE;
    canvas.height = DONOR_PROBE;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(bitmap, 0, 0, DONOR_PROBE, DONOR_PROBE);
    bitmap.close();
    return ctx.getImageData(0, 0, DONOR_PROBE, DONOR_PROBE).data;
  };
  const [tileData, donorData] = await Promise.all([
    fetch(tileUrl)
      .then((r) => r.blob())
      .then(draw),
    draw(donorBlob)
  ]);
  let sum = 0;
  for (let i = 0; i < tileData.length; i += 4) {
    // Rec. 601 luma, matching sharp's greyscale in the research script.
    const a = 0.299 * tileData[i] + 0.587 * tileData[i + 1] + 0.114 * tileData[i + 2];
    const b = 0.299 * donorData[i] + 0.587 * donorData[i + 1] + 0.114 * donorData[i + 2];
    sum += Math.abs(a - b);
  }
  return sum / (tileData.length / 4);
}

/** Downscale to ≤1024 on the long side as JPEG — plenty for the identity
 *  model (it was validated at exactly this size) and far kinder to upload
 *  time than a 12MP camera original. EXIF orientation is honored so a phone
 *  portrait does not arrive sideways. */
async function preparePhoto(file) {
  let bmp = null;
  try {
    bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // Some browsers reject the options bag or the codec; try the plain form
    // before giving up (orientation may be lost, which only affects framing).
    bmp = await createImageBitmap(file).catch(() => null);
  }
  if (!bmp) throw new Error('Could not read that image — try a JPG or PNG.');
  const scale = Math.min(1, 1024 / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(bmp, 0, 0, w, h);
  bmp.close();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
  if (!blob) throw new Error('Could not process that image.');
  return blob;
}

function setPhoto(blob) {
  if (photoThumb.src) URL.revokeObjectURL(photoThumb.src);
  photoBlob = blob;
  if (blob) {
    photoThumb.src = URL.createObjectURL(blob);
    photoThumb.classList.remove('hidden');
    photoChooseLabel.textContent = 'Photo loaded — tap to swap';
    photoClear.classList.remove('hidden');
    photoEcho.textContent = '';
  } else {
    photoThumb.removeAttribute('src');
    photoThumb.classList.add('hidden');
    photoChooseLabel.textContent = 'Choose a photo…';
    photoClear.classList.add('hidden');
  }
}

photoChoose.onclick = () => photoInput.click();
photoClear.onclick = () => {
  setPhoto(null);
  photoInput.value = '';
  photoEcho.textContent = '';
};
photoInput.onchange = async () => {
  const file = photoInput.files?.[0];
  if (!file) return;
  photoEcho.textContent = 'Preparing photo…';
  try {
    setPhoto(await preparePhoto(file));
  } catch (err) {
    setPhoto(null);
    photoEcho.textContent = String(err.message || err);
  }
};

function populatePicker() {
  for (const s of subjects) {
    const opt = document.createElement('option');
    opt.value = s.id;
    opt.textContent = `${s.emoji} ${s.label}`;
    selectEl.appendChild(opt);
  }
  for (const st of styles) {
    const opt = document.createElement('option');
    opt.value = st.id;
    opt.textContent = `${st.emoji} ${st.label}`;
    styleEl.appendChild(opt);
  }
  const saved = localStorage.getItem(CHOICE_KEY);
  if (saved && [...selectEl.options].some((o) => o.value === saved)) selectEl.value = saved;
  const savedStyle = localStorage.getItem(STYLE_KEY);
  if (savedStyle && [...styleEl.options].some((o) => o.value === savedStyle))
    styleEl.value = savedStyle;
  styleEl.onchange = () => {
    localStorage.setItem(STYLE_KEY, styleEl.value);
    syncPicker();
  };
  syncPicker();
}

function syncPicker() {
  // An exact prompt is sent verbatim, so the style picker (which would append
  // to it) has nothing to act on — hide it rather than let it lie.
  const raw = selectEl.value === 'raw';
  const photo = selectEl.value === 'photo';
  customRow.classList.toggle('hidden', selectEl.value !== 'custom');
  photoRow.classList.toggle('hidden', !photo);
  rawRow.classList.toggle('hidden', !raw);
  styleField.classList.toggle('hidden', raw);
  applyStyleMenu(photo);
  customStyleRow.classList.toggle('hidden', raw || styleEl.value !== 'custom');
  localStorage.setItem(CHOICE_KEY, selectEl.value);
}

// Photo mode renders only the styles the identity recipe is proven on
// (config.photo.styles), so the menu narrows to those. The visitor's saved
// choice is left alone in localStorage; it comes back when they leave photo
// mode.
function applyStyleMenu(photo) {
  const allowed = photo ? new Set(config?.photo?.styles || []) : null;
  for (const opt of styleEl.options) {
    const usable =
      !allowed || opt.value === 'random' || (opt.value !== 'custom' && allowed.has(opt.value));
    opt.hidden = !usable;
    opt.disabled = !usable;
  }
  if (allowed && styleEl.selectedOptions[0]?.disabled) styleEl.value = 'random';
  if (!allowed) {
    const saved = localStorage.getItem(STYLE_KEY);
    if (saved && [...styleEl.options].some((o) => o.value === saved && !o.disabled))
      styleEl.value = saved;
  }
}
selectEl.onchange = () => {
  syncPicker();
  customEcho.textContent = '';
  // The estimate depends on which model renders, and photo mode switches it.
  refreshCostHint();
};

// Resolve the picker into a subject: a catalog entry, a random one, or a
// custom idea run through the LLM and composed server-side.
async function resolveSubject() {
  const choice = selectEl.value;
  // Exact prompt: no LLM shaping, no clause stack, no style — the text the
  // visitor typed is the whole prompt. Id 'custom' keeps promptsForBatch from
  // swapping in random styles over it, same as the inspector's reroll.
  if (choice === 'raw') {
    const prompt = rawInput.value.trim();
    if (!prompt) throw new Error('Type the prompt you want rendered first.');
    const label = prompt.length > 48 ? `${prompt.slice(0, 48).trimEnd()}…` : prompt;
    return { id: 'custom', label, emoji: '📝', prompt };
  }
  if (choice === 'photo') {
    if (!photoBlob) {
      throw new Error(
        'Choose a photo first — it stays in your browser, sent only to the renderer.'
      );
    }
    const res = await fetch(`/api/prompt?subject=photo&style=${encodeURIComponent(styleEl.value)}`);
    if (!res.ok) throw new Error('could not compose the photo prompt');
    const { prompt, style } = await res.json();
    const styleLabel = styles.find((st) => st.id === style);
    return {
      id: 'photo',
      photo: true,
      // The style resolved for THIS roll, not the picker's value: with "Any
      // style" the server chose, and the donor sent as Reference A has to be
      // that same style's topology. One donor per project, so the whole batch
      // shares it and only the seed varies.
      photoStyle: style,
      label: styleLabel ? `Your Photo · ${styleLabel.label}` : 'Your Photo',
      emoji: '📷',
      prompt
    };
  }
  const customStyle = styleEl.value === 'custom' ? customStyleInput.value.trim() : '';
  if (styleEl.value === 'custom' && !customStyle) {
    throw new Error('Describe the custom style you want first.');
  }
  if (choice === 'custom') {
    const idea = customInput.value.trim();
    if (!idea) throw new Error('Type what you want tessellated first.');
    statusEl.textContent = 'Shaping your idea into a tileable subject…';
    const phrase = await normalizeSubject(idea, allowMature());
    if (!phrase) throw new Error('Could not turn that into a subject — try rewording it.');
    const res = await fetch('/api/compose', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject: phrase, style: styleEl.value, customStyle })
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'compose failed');
    const { prompt } = await res.json();
    customEcho.textContent = `→ ${phrase}`;
    return { id: 'custom', label: phrase, emoji: '✨', prompt };
  }
  const randomSubject = choice === 'random';
  const subject = randomSubject
    ? subjects[Math.floor(Math.random() * subjects.length)]
    : subjects.find((s) => s.id === choice) || subjects[0];
  const styleId = styleEl.value;
  const res = await fetch(
    `/api/prompt?subject=${encodeURIComponent(subject.id)}` +
      `&style=${encodeURIComponent(styleId)}` +
      (customStyle ? `&customStyle=${encodeURIComponent(customStyle)}` : '')
  );
  if (!res.ok) throw new Error('could not compose that subject');
  const { prompt, style } = await res.json();
  const styleLabel =
    style === 'custom' ? { label: customStyle } : styles.find((st) => st.id === style);
  return {
    ...subject,
    prompt,
    label: randomSubject
      ? 'Surprise me'
      : styleEl.value === 'random'
        ? `${subject.label} · Surprise me`
        : styleLabel
          ? `${subject.label} · ${styleLabel.label}`
          : subject.label,
    emoji: randomSubject ? '🎲' : subject.emoji,
    randomSubject
  };
}

// Draw without replacement until the pool is exhausted. A four-slot Surprise
// me batch should actually show four different choices rather than occasionally
// rolling the same catalog entry two or three times.
function randomChoices(pool, n) {
  const shuffled = [...pool];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return Array.from({ length: n }, (_, i) => shuffled[i % shuffled.length]);
}

// Slot prompts for one batch. Subject and style each vary independently when
// their picker says Surprise me. Dynamic-prompt syntax still packs the four
// combinations into one project, so this does not add queue round trips.
async function promptsForBatch(subject, n) {
  const randomStyle = styleEl.value === 'random';
  // Photo slots compose per-slot even for a fixed style: the server coin-flips
  // nouveau between its two proven recipes, and a random style should vary
  // across the batch — both are server-side choices, so each slot asks anew.
  // A photo batch is one style, one donor, one prompt — a seed lottery, like
  // any other fixed-style roll. Composing per slot would let slots disagree
  // about which donor the single contextImages set is carrying.
  if (subject.id === 'photo') {
    return Array.from({ length: n }, () => subject.prompt);
  }
  if ((!randomStyle && !subject.randomSubject) || subject.id === 'custom') {
    return Array.from({ length: n }, () => subject.prompt);
  }
  const subjectPicks = subject.randomSubject
    ? randomChoices(subjects, n)
    : Array.from({ length: n }, () => subject);
  const stylePicks = randomStyle
    ? randomChoices(
        styles.filter((st) => st.random !== false),
        n
      )
    : Array.from({ length: n }, () => ({ id: styleEl.value }));
  const composed = await Promise.all(
    subjectPicks.map((pickedSubject, i) =>
      fetch(
        `/api/prompt?subject=${encodeURIComponent(pickedSubject.id)}` +
          `&style=${encodeURIComponent(stylePicks[i].id)}`
      )
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
    )
  );
  return composed.map((c) => c?.prompt || subject.prompt);
}

goBtn.onclick = async () => {
  if (activeRoll) {
    activeRoll.abort();
    statusEl.textContent = 'Stopping…';
    return;
  }
  if (!isAuthed) {
    statusEl.textContent = 'Sign in with your Sogni account to render your own tiles.';
    return;
  }
  goBtn.disabled = true;
  winnerEl.classList.add('hidden');
  let subject;
  try {
    subject = await resolveSubject();
  } catch (err) {
    statusEl.textContent = String(err.message || err);
    goBtn.disabled = false;
    return;
  }
  await startRoll(subject);
};

const goButtonMarkup = goBtn.innerHTML;

function setRolling(running) {
  goBtn.disabled = false;
  goBtn.classList.toggle('is-stop', running);
  goBtn.setAttribute('aria-label', running ? 'Stop rendering' : 'Tessellate');
  goBtn.innerHTML = running ? `${icon('close', 18)} Stop` : goButtonMarkup;
}

async function startRoll(subject) {
  if (activeRoll) return;
  activeRoll = new AbortController();
  setRolling(true);
  try {
    await rollSubject(subject, activeRoll.signal);
  } finally {
    activeRoll = null;
    setRolling(false);
  }
}

// The whole batch flow — render, score, pick a winner, upsell on empty
// pockets — for one resolved subject. Shared by the Go button and the prompt
// inspector's "Render this prompt", which arrives with its own prompt text.
async function rollSubject(subject, signal) {
  // Captured once so clearing the photo mid-roll cannot strand a reroll batch
  // without its context image.
  const photoContext = subject.photo ? photoBlob : null;
  if (subject.photo && !photoContext) {
    statusEl.textContent =
      'Choose a photo first — it stays in your browser, sent only to the renderer.';
    return;
  }
  statusEl.textContent = `${subject.emoji} Rolling ${subject.label}…`;
  slotHint.classList.remove('hidden');

  let winner = null;
  let hitInsufficient = false;
  let renderError = null;

  // A newly created account has a real session but no spendable credits until
  // its email reward is claimed. Do not create doomed projects and leave their
  // eventual 404 lookups masquerading as a bad roll.
  const billing = await accountBilling();
  if (hasKnownEmptyBalance(billing)) {
    statusEl.textContent =
      'No Spark available yet. Verify your email to claim credits, or add Spark.';
    if (await refreshMonthlyBoost(billing.subscription?.status)) openMonthlyBoost();
    else showUpsell();
    refreshAccountChip(currentUsername);
    return;
  }

  for (
    let batch = 1;
    batch <= config.maxBatches && !winner && !hitInsufficient && !renderError;
    batch++
  ) {
    if (signal.aborted) break;
    if (batch > 1) statusEl.textContent = `No seamless tile yet. Batch ${batch}, rerolling…`;
    resetSlots();
    // One project for the whole batch. When the style is left to chance the
    // slots carry different styles, expanded server-side by dynamic-prompt
    // syntax; otherwise every slot shares the prompt and only the seed varies.
    const prompts = await promptsForBatch(subject, config.batchSize);
    const seed = randomSeed();
    const scored = [];
    try {
      // Photo tiles render on Identity Edit with a Krea 2 Turbo topology donor
      // as Image 1 and the visitor's photo as Image 2. The model picker and its
      // knobs deliberately do not apply.
      const photoMode = !!subject.photo;
      if (photoMode && !config.photo)
        throw new Error('Photo mode is warming up — try again in a minute.');
      const picked = photoMode
        ? {
            model: config.photo.model,
            sampler: config.photo.sampler,
            scheduler: config.photo.scheduler
          }
        : selectedModel();
      // Held so each finished tile can be measured against the exact donor it
      // was edited from, without refetching it per slot.
      const donorBlob = photoMode ? await photoDonor(subject.photoStyle) : null;
      await renderBatch({
        model: picked.model,
        prompts,
        seed,
        size: config.size,
        vae: picked.vae,
        sampler: picked.sampler,
        scheduler: picked.scheduler,
        ...(photoMode
          ? {
              steps: config.photo.steps,
              guidance: config.photo.guidance,
              contextImages: donorBlob ? [donorBlob, photoContext] : [photoContext],
              ...(donorBlob
                ? {
                    groundingPx: config.photo.groundingPx,
                    refBoost: config.photo.refBoost,
                    refBoostA: config.photo.refBoostA
                  }
                : {})
            }
          : {}),
        loras: picked.loras,
        loraStrengths: picked.loraStrengths,
        outputFormat: selectedFormat(),
        allowMature: allowMature(),
        signal,
        onTile: async (url, i, usedPrompt, ids) => {
          if (signal.aborted) return;
          try {
            const score = await scoreUrl(url, signal);
            if (signal.aborted) return;
            // The seam gate cannot see this one: a donor handed back unedited
            // is a genuinely seamless tile, so it passes and then wins. Measure
            // it before the verdict is used for anything.
            // A measurement that could not be taken must not read as "edited":
            // failing open here is how a tile without the visitor's face wins.
            // 0 (rather than Infinity) makes an unmeasurable tile fail closed.
            const untouched =
              donorBlob &&
              (await donorDistance(url, donorBlob).catch((err) => {
                console.warn('donor distance failed, rejecting the tile:', err?.message || err);
                return 0;
              })) < (config.photo.minDonorDistance ?? MIN_DONOR_DISTANCE);
            if (signal.aborted) return;
            const tile = {
              seed,
              url,
              label: subject.label,
              emoji: subject.emoji,
              prompt: usedPrompt || prompts[i] || subject.prompt,
              model: picked.model,
              // Which platform job made this tile. Only useful for one thing —
              // naming the generation in a content report — but it has to be
              // captured here, because the ids are gone once the batch ends.
              jobId: ids?.jobId,
              projectId: ids?.projectId,
              // Which donor topology this came from, so rerolling its prompt
              // later reaches for the same one.
              ...(subject.photoStyle ? { photoStyle: subject.photoStyle } : {}),
              ...score,
              // Overrides the seam verdict deliberately: whatever the wrap
              // looks like, a tile that never received the visitor's face is
              // not a candidate for a feature whose point is their face.
              ...(untouched ? { verdict: 'unchanged' } : {})
            };
            batchTiles.push(tile);
            markSlot(i, tile);
            scored.push(tile);
          } catch (err) {
            if (signal.aborted || err?.name === 'AbortError') return;
            markSlot(i, { verdict: 'error' });
          }
        }
      });
    } catch (err) {
      if (err?.name === 'AbortError') break;
      if (isInsufficientFunds(err)) hitInsufficient = true;
      else renderError = err;
      failPendingSlots();
    }
    winner = scored.find((t) => t.verdict === 'pass') || null;
  }

  if (signal.aborted) {
    statusEl.textContent = 'Stopped.';
  } else if (winner) {
    // Winner background + blob for persistence (network URLs expire).
    const blob = await fetch(winner.url)
      .then((r) => r.blob())
      .catch(() => null);
    const src = blob ? URL.createObjectURL(blob) : winner.url;
    setBackground(src);
    enterWorkingPhase();
    confetti(subject.emoji);
    statusEl.textContent = '';
    winnerEl.classList.remove('hidden');
    winnerLabel.textContent = `${subject.emoji} ${subject.label}`;
    setKeepButton('keep');
    current = blob
      ? {
          id: `fav-${Date.now()}`,
          blob,
          src,
          label: subject.label,
          emoji: subject.emoji,
          seed: winner.seed,
          prompt: winner.prompt || subject.prompt,
          model: winner.model,
          // Kept so an opted-in Keep can publish from the original Sogni URL,
          // which the server re-fetches and re-scores.
          remoteUrl: winner.url,
          // Carried into the keeper so a tile saved today can still be
          // reported to the platform tomorrow, by name of its job.
          jobId: winner.jobId,
          projectId: winner.projectId,
          ...(winner.photoStyle ? { photoStyle: winner.photoStyle } : {}),
          favorited: false
        }
      : null;
  } else if (hitInsufficient) {
    statusEl.textContent = 'Not enough Spark to keep rolling.';
    if (await refreshMonthlyBoost(billing.subscription?.status)) openMonthlyBoost();
    else showUpsell();
  } else if (renderError) {
    const detail = String(renderError.message || renderError).slice(0, 100);
    statusEl.textContent = detail
      ? `The render could not start: ${detail}`
      : 'The render could not start. Please try again.';
  } else {
    const nears = batchTiles.filter((t) => t.verdict === 'near').length;
    const unchanged = batchTiles.filter((t) => t.verdict === 'unchanged').length;
    // "The dice were cold" is true of a seam miss and misleading here: these
    // tiles came back without the visitor's face at all, and a clearer photo
    // is the thing that actually helps.
    statusEl.textContent = unchanged
      ? `The renderer returned the pattern without your face ${unchanged === batchTiles.length ? 'every time' : `${unchanged} times`}. Spin again, or try a clearer, front-facing photo.`
      : nears
        ? `No perfect tile, but ${nears} came close. Click one to see it full screen.`
        : 'The dice were cold. Spin again.';
  }
  refreshAccountChip(el('account-user').textContent);
}

// ---------- confetti ----------
// A visitor who has asked their system to reduce motion has asked for exactly
// this. Nothing is lost: the tile is already on screen and the status line
// already says what happened.
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

function confetti(emoji) {
  if (REDUCED_MOTION.matches) return;
  const burst = document.createElement('div');
  burst.className = 'burst';
  for (let i = 0; i < 26; i++) {
    const s = document.createElement('span');
    s.textContent = emoji;
    s.style.left = `${Math.random() * 100}vw`;
    s.style.animationDuration = `${2 + Math.random() * 2.4}s`;
    s.style.animationDelay = `${Math.random() * 0.7}s`;
    burst.appendChild(s);
  }
  document.body.appendChild(burst);
  setTimeout(() => burst.remove(), 5200);
}

// ---------- favorites ----------
// One keep flow serves both entry points: the winner card in the drawer and
// the rail heart on an upscale. The record is fixed up front so the async
// publish paints the right control even if the user has moved on.
async function keepCurrent(rec = current) {
  if (!rec || rec.favorited) return;
  await saveFavorite(rec);
  rec.favorited = true;
  // An upscale keeps the id of the keeper it descends from, so the save
  // above overwrote that record. The shelf caches an object URL per id;
  // drop the stale one or the shelf keeps showing the old pixels.
  if (objectUrls.has(rec.id)) {
    URL.revokeObjectURL(objectUrls.get(rec.id));
    objectUrls.delete(rec.id);
    favPrompts.delete(rec.id);
  }
  if (current === rec) setKeepButton('saved');
  updateUpscaleUI();
  renderShelf();

  // Sharing follows the decision to keep, never the render itself: a tile the
  // user has not chosen is not something they have agreed to publish.
  if (!publishEnabled() || !rec.remoteUrl) return;
  publishTile({
    url: rec.remoteUrl,
    subject: `${rec.emoji} ${rec.label}`,
    prompt: rec.prompt,
    seed: rec.seed,
    // The stream entry this image already occupies, if any — an upscale of a
    // published tile updates it in place rather than standing next to it.
    replaces: rec.streamId || undefined
  })
    .then((res) => {
      rec.shared = true;
      rec.streamId = res.entry?.id || rec.streamId;
      // Persist the entry id with the keeper so an upscale kept in a later
      // session still knows which wall tile it should update — unless the
      // keeper was removed while the publish was in flight, in which case a
      // re-save would resurrect it.
      if (favIndex().some((f) => f.id === rec.id)) saveFavorite(rec).catch(() => {});
      if (current === rec) setKeepButton('shared');
      updateUpscaleUI();
      if (activeTab === 'stream') renderStream();
    })
    .catch((err) => {
      console.warn('publish failed:', err.message);
    });
}

// Wrapped so the click event is not mistaken for a record argument.
favBtn.onclick = () => keepCurrent();

// Prompts live only in IndexedDB (the localStorage index stays small), so
// the stored record is read whenever a favorite is surfaced.
const favPrompts = new Map();

async function favoriteItems() {
  const list = [];
  for (const f of favIndex()) {
    const rec = await loadFavorite(f.id);
    if (!rec?.blob) continue;
    if (!objectUrls.has(f.id)) {
      objectUrls.set(f.id, URL.createObjectURL(rec.blob));
    }
    favPrompts.set(f.id, rec.prompt || null);
    list.push({
      id: f.id,
      src: objectUrls.get(f.id),
      label: `${f.emoji || '♥'} ${f.label}`,
      name: `${f.emoji || '♥'} ${f.label}`,
      prompt: favPrompts.get(f.id),
      seed: f.seed,
      record: rec,
      shared: !!rec.streamId
    });
  }
  return list;
}

// Picking a tile only changes the background — it never takes over the
// screen. Going fullscreen stays an explicit choice (click the background),
// and when you do, it opens on the tile you picked and pages through the
// collection you picked it from.
let activeCollection = { items: [], index: 0 };

// Touch devices get taken straight to fullscreen. On a desktop, picking a
// tile reveals it around the card and that is the whole feedback loop; on a
// phone the card covers the viewport, so the tap appeared to do nothing at
// all — the tester reported never seeing what they had generated.
const COARSE_POINTER = window.matchMedia('(pointer: coarse)').matches;

// Hovering a keeper or a stream tile used to swap the page background and put
// it back on the way out. Changing the backdrop is the app's one big gesture,
// and having it fire on a pointer merely crossing a thumbnail meant the page
// flickered through tiles nobody had chosen — while scrolling a row, or on the
// way to a control. Picking a tile is a click now; the hover treatment on the
// thumbnail itself is what says it is clickable.

function selectFromCollection(items, index) {
  if (!items.length) return;
  // The menu is positioned against the viewport, so leaving it open while the
  // page goes fullscreen would strand it over the preview.
  closeActionMenu();
  activeCollection = { items, index: Math.max(0, index) };
  setBackground(items[activeCollection.index].src);
  enterWorkingPhase();
  if (COARSE_POINTER) previewCollection(items, activeCollection.index);
}

// Whatever tile is on screen when you leave fullscreen stays as the
// background; reverting would read as the app undoing your choice.
// Promoting a tile to your keepable/publishable *result* is separate and
// explicit (the "Make this my result" button).
function previewCollection(items, index) {
  if (!items.length) return;
  enterPreview(items, Math.max(0, index));
}

async function renderShelf() {
  closeActionMenu();
  const list = await favoriteItems();
  el('favs-empty').classList.toggle('hidden', list.length > 0);
  favsEl.innerHTML = '';
  const previewItems = list.map((f) => ({
    src: f.src,
    label: f.label,
    name: f.name || f.label,
    prompt: f.prompt,
    seed: f.seed,
    removeLabel: 'Remove from your keepers',
    onRemove: async () => {
      await removeFavorite(f.id);
      renderShelf();
      return true;
    },
    // A keeper is the visitor's own generation, so its report goes to the
    // platform. Keepers saved before ids were captured cannot name a job, so
    // they offer no report rather than sending one nobody can action.
    report: f.record?.jobId
      ? {
          kind: 'generation',
          projectId: f.record.projectId,
          jobId: f.record.jobId,
          url: f.record.remoteUrl,
          prompt: f.record.prompt,
          model: f.record.model,
          seed: f.record.seed
        }
      : null
  }));
  list.forEach((f, i) => {
    const tile = document.createElement('div');
    tile.className = 'fav-tile';
    tile.title = f.label;
    // The image goes in via innerHTML first; addOpen then appends the tile's
    // click target. Doing it the other way round re-parsed the tile-open button
    // out of a fresh innerHTML assignment, dropping its onclick — so a keeper
    // clicked did nothing, unlike the stream and samples tiles built this way.
    tile.innerHTML = tiled(f.src);
    addOpen(tile, `Show ${f.label}`, () => selectFromCollection(previewItems, i));
    addActionMenu(tileActions(tile), () => [
      ...commonTileItems({ src: f.src, name: f.name || f.label, seed: f.seed, entry: f }),
      !f.shared && {
        label: 'Share to the public stream',
        icon: 'globe',
        onClick: async () => {
          statusEl.textContent = 'Sharing to the stream…';
          try {
            const result = await publishKeeperTile({
              blob: f.record.blob,
              subject: f.name,
              prompt: f.record.prompt,
              seed: f.record.seed
            });
            f.record.streamId = result.entry?.id;
            await saveFavorite(f.record);
            statusEl.textContent = 'Shared to the public stream.';
            if (activeTab === 'stream') renderStream();
            await renderShelf();
          } catch (err) {
            statusEl.textContent = `Share failed — ${String(err.message || err).slice(0, 120)}`;
            console.warn('keeper publish failed:', err.message);
          }
        }
      },
      // A keeper is the visitor's own render, so a report on it is a report to
      // the platform about a generation — the same destination sogni-web uses.
      // Only ids captured at render time make it actionable, so tiles kept
      // before this existed offer no report.
      f.record?.jobId && {
        label: 'Report…',
        icon: 'flag',
        // Keepers live in IndexedDB and outlive the session, so this menu is
        // reachable signed out — where the platform would just 401 the report
        // after the visitor had filled the form in.
        disabled: !isAuthed,
        note: isAuthed ? '' : 'Sign in first',
        onClick: () =>
          openReportModal({
            kind: 'generation',
            name: f.name || f.label,
            projectId: f.record.projectId,
            jobId: f.record.jobId,
            url: f.record.remoteUrl,
            prompt: f.record.prompt,
            model: f.record.model,
            seed: f.record.seed
          })
      },
      {
        label: 'Remove from keepers',
        icon: 'trash',
        danger: true,
        onClick: async () => {
          await removeFavorite(f.id);
          renderShelf();
        }
      }
    ]);
    favsEl.appendChild(tile);
  });
}

// ---------- tabs: keepers vs public stream ----------
let activeTab = 'keepers';

// The wall is paged, not loaded whole: it can hold hundreds of tiles and a
// visitor rarely wants all of them. We keep what has been fetched so far, the
// server's total, and the offset to ask for next.
const STREAM_PAGE = 30;
let streamItems = [];
let streamTotal = 0;
let streamOffset = 0;
let streamLoading = false;

const TABS = ['samples', 'keepers', 'stream'];

// Real tabs: role and aria-selected, not a class called "active". A screen
// reader had no way to tell these buttons apart from any other button, or to
// know which panel they governed.
function selectTab(tab) {
  activeTab = tab;
  for (const name of TABS) {
    el(`tab-${name}`).setAttribute('aria-selected', String(name === tab));
    el(`panel-${name}`).classList.toggle('hidden', name !== tab);
  }
  el('stream-sort').classList.toggle('hidden', tab !== 'stream');
  el('publish-toggle').classList.toggle('hidden', tab !== 'keepers' || !isAuthed);
  if (tab === 'stream') renderStream();
  // The panel's tab strip and the spine name the same collection, so a switch
  // from either keeps the spine's active icon in step. Guarded because selectTab
  // also runs during boot, before the deck controller has initialised.
  if (typeof deckSection !== 'undefined' && deckSection !== tab) {
    deckSection = tab;
    paintSpine();
  }
}
for (const name of TABS) el(`tab-${name}`).onclick = () => selectTab(name);
// Changing the sort re-pages from the top: an offset into one ordering is
// meaningless in another.
el('stream-sort').onchange = () => renderStream();

// ---------- deck: the left spine and the sliding studio ----------
// The app opens minimized: a rail of icons on the left, the pattern owning the
// rest of the screen. A spine icon slides the studio out over the pattern;
// collapsing hands the whole screen back to the tiling. It never opens on its
// own — minimized is the resting state, restored on every load.
const deckEl = el('deck');
const spineBrand = el('spine-brand');
const deckPanelBody = document.querySelector('.panel-body');
const COLLECTION_SECTIONS = ['samples', 'keepers', 'stream'];
let deckSection = 'create';

function paintSpine() {
  const open = deckEl.dataset.open === 'true';
  spineBrand.setAttribute('aria-expanded', String(open));
  for (const btn of document.querySelectorAll('.spine-btn')) {
    btn.setAttribute('aria-current', String(open && btn.dataset.section === deckSection));
  }
}

function openDeck(section) {
  if (section) deckSection = section;
  // Opening the studio retires the first-run pitch for the rest of the session,
  // even after the studio closes again.
  document.body.classList.add('studio-seen');
  // A collection icon drives the drawer's own tab, so the spine and the tab
  // strip never disagree about what is on screen.
  if (COLLECTION_SECTIONS.includes(deckSection)) selectTab(deckSection);
  deckEl.dataset.open = 'true';
  // Mirrored on the body so page-level chrome (the zoom rail on phones)
  // can react to the studio being open without reaching into the deck.
  document.body.classList.add('deck-open');
  paintSpine();
  // Bring the requested section to the top of the scrolling panel.
  requestAnimationFrame(() => {
    const target = deckSection === 'create' ? el('dock') : el('drawer');
    if (deckPanelBody && target && !target.classList.contains('hidden')) {
      deckPanelBody.scrollTo({ top: Math.max(0, target.offsetTop - 16), behavior: 'smooth' });
    }
  });
}

function closeDeck() {
  deckEl.dataset.open = 'false';
  document.body.classList.remove('deck-open');
  paintSpine();
}

function toggleDeck(section) {
  const open = deckEl.dataset.open === 'true';
  if (open && (!section || section === deckSection)) closeDeck();
  else openDeck(section);
}

spineBrand.onclick = () => toggleDeck();
el('panel-collapse').onclick = closeDeck;
el('deck-scrim').onclick = closeDeck;
for (const btn of document.querySelectorAll('.spine-btn')) {
  btn.onclick = () => {
    const section = btn.dataset.section;
    // Nothing to render until you are signed in, so Create becomes the sign-in
    // prompt rather than opening an empty generator.
    if (section === 'create' && !isAuthed) {
      openAuthModal('signup');
      return;
    }
    toggleDeck(section);
  };
}

// Escape collapses the studio, but only when it is the frontmost surface: a
// modal, the account menu and fullscreen preview each own Escape ahead of it.
// Captured so this runs before those bubble-phase handlers and can defer to a
// still-open one, rather than seeing it already closed and stealing the press.
document.addEventListener(
  'keydown',
  (e) => {
    if (e.key !== 'Escape' || deckEl.dataset.open !== 'true') return;
    if (document.body.classList.contains('preview')) return;
    if (document.querySelector('.modal:not(.hidden)')) return;
    if (!el('auth-modal').classList.contains('hidden')) return;
    if (!accountMenu.classList.contains('hidden')) return;
    // An open tile menu owns Escape too. It cannot defend itself from here:
    // its own handler is on the bubble phase, and this one captures, so
    // without this line dismissing a menu also collapsed the studio out from
    // under the grid the visitor was working in.
    if (document.querySelector('.tile-menu')) return;
    closeDeck();
  },
  { capture: true }
);

// Lazy load: page in the next tiles as the visitor scrolls the row toward its
// end, so browsing the wall is one continuous sideways scroll (like Your
// Keepers) rather than a click-to-load step.
el('stream-grid').addEventListener('scroll', maybeLoadMoreOnScroll);
function maybeLoadMoreOnScroll() {
  if (streamLoading || streamItems.length >= streamTotal) return;
  const grid = el('stream-grid');
  // Within ~two tiles of the right edge — fetch the next page before the
  // visitor actually hits the end.
  if (grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 240) {
    loadStreamPage({ reset: false });
  }
}

// A tile with no byline predates nothing and belongs to no one visible — say
// nothing rather than inventing an "anonymous" author.
function byline(author) {
  return author ? ` · by ${author}` : '';
}

function canDeleteTile(item) {
  return canModerate || (!!currentUsername && item.author === currentUsername);
}

// Reset re-pages from the start (first load, sort change); otherwise this
// appends the next page to the row. Guarded so overlapping scroll events do
// not fetch the same page twice.
async function loadStreamPage({ reset }) {
  if (streamLoading) return;
  streamLoading = true;
  const emptyEl = el('stream-empty');
  if (reset) {
    streamOffset = 0;
    streamItems = [];
    streamTotal = 0;
  }
  try {
    const { total, entries } = await fetchStream({
      sort: el('stream-sort').value,
      limit: STREAM_PAGE,
      offset: streamOffset
    });
    streamTotal = total;
    streamItems.push(...entries);
    streamOffset += entries.length;
    // A reset starts the row over at the left; an append keeps the visitor's
    // scroll position instead of snapping back to the start.
    renderStreamGrid({ preserveScroll: !reset });
    emptyEl.classList.toggle('hidden', streamItems.length > 0);
    emptyEl.textContent = 'Nothing published yet. Be the first.';
    updateStreamCount();
  } catch (err) {
    if (reset) {
      streamItems = [];
      renderStreamGrid();
    }
    emptyEl.classList.remove('hidden');
    emptyEl.textContent = `Stream unavailable: ${err.message}`;
    el('stream-count').textContent = '';
  } finally {
    streamLoading = false;
  }
  // If a short first page did not fill the row, there is nothing to scroll and
  // the lazy trigger can never fire — top it up until it scrolls or runs dry.
  if (!reset || streamItems.length >= streamTotal) return;
  const grid = el('stream-grid');
  if (grid.scrollWidth <= grid.clientWidth) loadStreamPage({ reset: false });
}
function renderStream() {
  return loadStreamPage({ reset: true });
}

function updateStreamCount() {
  // `offset` can exceed the fetched count once deletes shrink the wall, so the
  // shown count is what we actually hold, not the running offset.
  el('stream-count').textContent = streamTotal ? `${streamItems.length} of ${streamTotal}` : '';
}

function renderStreamGrid({ preserveScroll = false } = {}) {
  closeActionMenu();
  const grid = el('stream-grid');
  // Clearing innerHTML zeroes the scroll, which yanks a sideways-scrolled row
  // back to the start; capture and restore scrollLeft so lazy-load and delete
  // leave the visitor where they were.
  const priorScroll = preserveScroll ? grid.scrollLeft : 0;
  grid.innerHTML = '';
  // The hearts and deletes ride into fullscreen with each item: on touch the
  // thumbnails carry no overlay controls at all, so the preview is where a
  // visitor votes, saves, or removes.
  const previewItems = streamItems.map((it) => ({
    src: it.url,
    label: `${it.subject}${byline(it.author)}`,
    name: it.subject,
    prompt: it.prompt,
    seed: it.seed,
    heart: {
      favorites: it.favorites,
      favorited: it.favorited,
      toggle: isAuthed
        ? async () => {
            const updated = await toggleStreamFavorite(it.id);
            it.favorites = updated.favorites;
            it.favorited = updated.favorited;
            renderStreamGrid({ preserveScroll: true });
            return updated;
          }
        : null
    },
    removeLabel: 'Remove from the public stream',
    onRemove: canDeleteTile(it) ? () => deleteStreamItem(it) : null,
    // Fullscreen is the only route to a report on touch, where thumbnails
    // carry no controls. Your own tile offers none: removing it is the
    // action you actually want, and it is already here.
    report:
      isAuthed && !(currentUsername && it.author === currentUsername)
        ? {
            kind: 'stream',
            id: it.id,
            done: !!it.flagged,
            onSent: () => {
              it.flagged = true;
              renderStreamGrid({ preserveScroll: true });
            }
          }
        : null
  }));
  streamItems.forEach((item, i) => {
    const tile = document.createElement('div');
    tile.className = 'fav-tile';
    tile.title = `${item.subject}${byline(item.author)}`;
    tile.innerHTML = tiled(item.url);
    addOpen(tile, `Show ${item.subject}${byline(item.author)}`, () =>
      selectFromCollection(previewItems, i)
    );
    if (item.author) {
      const by = document.createElement('span');
      by.className = 'by';
      by.textContent = item.author;
      tile.appendChild(by);
    }
    // A tile withheld by reports is only ever visible to a moderator, and it
    // says so on the thumbnail — otherwise the wall would look unchanged and
    // the queue would be invisible.
    if (item.withheld) {
      const badge = document.createElement('span');
      badge.className = 'withheld-badge';
      badge.textContent = `Withheld · ${item.flagCount || 0} report${item.flagCount === 1 ? '' : 's'}`;
      badge.title = (item.flagReasons || []).join(', ');
      tile.appendChild(badge);
    }

    addActionMenu(tileActions(tile), () => {
      const mine = !!currentUsername && item.author === currentUsername;
      return [
        ...commonTileItems({
          src: item.url,
          name: item.subject,
          seed: item.seed,
          entry: { name: item.subject, prompt: item.prompt, seed: item.seed, size: item.size }
        }),
        // Reporting someone else's published tile is the point; reporting your
        // own is just a confusing way to delete it, and you already can.
        !mine && {
          label: item.flagged ? 'Reported' : 'Report…',
          icon: 'flag',
          disabled: !!item.flagged || !isAuthed,
          note: item.flagged ? 'Thank you' : isAuthed ? '' : 'Sign in first',
          onClick: () =>
            openReportModal({
              kind: 'stream',
              id: item.id,
              name: item.subject,
              onSent: () => {
                item.flagged = true;
                renderStreamGrid({ preserveScroll: true });
              }
            })
        },
        canModerate &&
          item.withheld && {
            label: 'Clear reports',
            icon: 'check',
            note: 'Moderator',
            onClick: async () => {
              try {
                await unflagStreamEntry(item.id);
                item.withheld = false;
                item.flagCount = 0;
                renderStreamGrid({ preserveScroll: true });
              } catch (err) {
                console.warn('unflag failed:', err.message);
              }
            }
          },
        canDeleteTile(item) && {
          label: mine ? 'Remove your tile' : 'Remove tile',
          icon: 'trash',
          danger: true,
          note: mine ? '' : 'Moderator',
          onClick: () => deleteStreamItem(item)
        }
      ];
    });

    const heart = document.createElement('button');
    heart.type = 'button';
    heart.className = `heart${item.favorited ? ' on' : ''}`;
    heart.innerHTML = `${icon('heart', 11)}<span>${item.favorites}</span>`;
    // Votes are counted per account, so a signed-out visitor is told why the
    // heart does nothing rather than being left to press it again.
    heart.title = isAuthed ? 'Love this tile' : 'Sign in with Sogni to vote';
    heart.onclick = async (e) => {
      e.stopPropagation();
      if (!isAuthed) {
        const emptyEl = el('stream-empty');
        emptyEl.classList.remove('hidden');
        emptyEl.textContent = 'Sign in with Sogni to vote for tiles.';
        return;
      }
      try {
        const updated = await toggleStreamFavorite(item.id);
        item.favorites = updated.favorites;
        item.favorited = updated.favorited;
        heart.querySelector('span').textContent = updated.favorites;
        heart.classList.toggle('on', updated.favorited);
      } catch (err) {
        console.warn(err.message);
      }
    };
    tile.appendChild(heart);
    grid.appendChild(tile);
  });
  if (priorScroll) grid.scrollLeft = priorScroll;
}

/** Confirm-and-delete a stream tile; true if it was removed. Shared by the
 *  grid's trash button and the fullscreen Remove. */
async function deleteStreamItem(item) {
  const mine = !!currentUsername && item.author === currentUsername;
  const whose = item.author ? `@${item.author}'s` : 'this';
  const msg = mine
    ? 'Remove your tile from the public stream? This cannot be undone.'
    : `Remove ${whose} tile from the public stream? You are doing this as a moderator.`;

  if (!window.confirm(msg)) return false;
  try {
    await deleteStreamEntry(item.id);
  } catch (err) {
    window.alert(`Could not remove that tile — ${err.message}`);
    return false;
  }
  streamItems = streamItems.filter((it) => it.id !== item.id);
  streamTotal = Math.max(0, streamTotal - 1);
  streamOffset = Math.max(0, streamOffset - 1);
  renderStreamGrid({ preserveScroll: true });
  updateStreamCount();
  el('stream-empty').classList.toggle('hidden', streamItems.length > 0);
  return true;
}

// ---------- stock samples (what signed-out visitors can play with) ----------
// Curated across two registers — beautiful and kitsch — and for colour
// range. Every one is a 1024x1024 render verified seamless at the
// calibrated threshold. Artistic ones lead: they tile better and they
// make a stronger first impression than a wall of rubber chickens.
const STOCK = [
  { file: 'cranes.png', label: '🕊️ Cranes · linocut' },
  { file: 'peacock.png', label: '🦚 Peacock · riso' },
  { file: 'koi.png', label: '🎏 Koi · ink' },
  { file: 'butterflies.png', label: '🦋 Butterflies · vector' },
  { file: 'foxes.png', label: '🦊 Foxes · ink' },
  { file: 'ravens.png', label: '🐦 Ravens · gouache' },
  { file: 'shells.png', label: '🐚 Shells · ink' },
  { file: 'portraits.png', label: '👩 Portraits · ink' },
  { file: 'beards.png', label: '🧔 Beards · photoreal' },
  { file: 'cats.png', label: '🐈 Cats · linocut' },
  { file: 'seahorses.png', label: '🐴 Seahorses · ink' },
  { file: 'skulls.png', label: '💀 Skulls · linocut' },
  { file: 'moths.png', label: '🌙 Luna Moths · ink' },
  { file: 'wolves.png', label: '🐺 Wolves · deco' },
  { file: 'hawks.png', label: '🦅 Hawks · deco' },
  { file: 'waves.png', label: '🌊 Great Wave · vector' },
  { file: 'lizards.png', label: '🦎 Lizards · ink' }
];

// Stock tiles are served immutable for a year, so changes ride a version
// bump. v4 adds the artistic set; v9 adds the photoreal beards tile the share
// card is built from.
const STOCK_VERSION = 9;

function renderSamples() {
  closeActionMenu();
  const row = el('samples-row');
  row.innerHTML = '';
  const previewItems = STOCK.map((s) => ({
    src: `assets/stock/${s.file}?v=${STOCK_VERSION}`,
    label: s.label,
    name: s.label
  }));
  STOCK.forEach((s, i) => {
    const tile = document.createElement('div');
    tile.className = 'fav-tile';
    tile.title = s.label;
    tile.innerHTML = tiled(previewItems[i].src);
    addOpen(tile, `Tile the page with ${s.label}`, () => selectFromCollection(previewItems, i));
    // Samples are tiles we ship, so download is the only action; the menu
    // stays for consistency with every other tile in the app.
    addActionMenu(tileActions(tile), () =>
      commonTileItems({ src: previewItems[i].src, name: s.label })
    );
    row.appendChild(tile);
  });
}

// ---------- publish toggle ----------
// The sensitive-content filter is off by default and toggled in Settings; the
// render code reads allowMature() per roll. See settings.js for why leaving it
// off is a content-policy choice with no effect on how tiles wrap.
const publishBtn = el('publish-toggle');
function paintPublishToggle() {
  publishBtn.setAttribute('aria-pressed', String(publishEnabled()));
}
publishBtn.onclick = () => {
  setPublishEnabled(!publishEnabled());
  paintPublishToggle();
};
paintPublishToggle();

// ---------- fullscreen preview ----------

/** Open the tiling fullscreen, over whichever collection the visitor was
 *  last browsing so prev/next walk something meaningful. */
async function openFullscreen() {
  const showing = currentBackground();
  if (!showing) return;

  // If a tile was picked from a grid, go fullscreen over that grid so
  // prev/next walk the collection the visitor was actually browsing.
  const active = activeCollection.items;
  if (active.length && active[activeCollection.index]?.src === showing) {
    previewCollection(active, activeCollection.index);
    return;
  }

  const list = [];
  if (current && !current.favorited) {
    list.push({
      src: current.src,
      label: `${current.emoji} ${current.label} (latest)`,
      name: `${current.emoji} ${current.label}`,
      prompt: current.prompt,
      seed: current.seed
    });
  }
  list.push(
    ...(await favoriteItems()).map((f) => ({
      src: f.src,
      label: f.label,
      name: f.name,
      prompt: f.prompt,
      seed: f.seed
    }))
  );
  if (!list.length && showing) {
    // A background with no record and no collection: there is nothing to say
    // about it, so fullscreen opens chromeless — the old "Current background"
    // caption told the visitor nothing they could not already see.
    list.push({ src: showing, label: '' });
  }
  previewCollection(
    list,
    list.findIndex((item) => item.src === showing)
  );
}

el('stage').addEventListener('click', (e) => {
  if (e.target !== el('stage')) return; // clicks on the panels don't count
  if (consumeCanvasDragClick()) return;
  openFullscreen();
});

// The stage click was the ONLY way in, and on a phone the panel covers the
// whole viewport — there is no stage left to click, so the tiling a visitor
// had just generated was unreachable. This button is always in the same
// place and needs no discovery.
el('view-fullscreen').onclick = openFullscreen;

// ---------- canvas chrome ----------
// Zoom and fullscreen both act on the pattern, so the whole rail waits until
// there is a pattern to act on.
const railEl = el('rail');
el('zoom-in').onclick = () => zoomCentered(getTileSize() * 1.25);
el('zoom-out').onclick = () => zoomCentered(getTileSize() / 1.25);

/** The dominant hue of a tile, as an OKLCH hue angle.
 *
 *  The chrome takes its neutrals from whatever is on screen at chroma 0.014:
 *  enough that the shell feels of a piece with the pattern behind it, never
 *  enough to read as a colour choice of its own or to compete with the tile.
 *  A fixed palette had to be either loud enough to fight a busy tile or dull
 *  enough to look grey against a vivid one. */
function tintFromTile(src) {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 8;
      canvas.height = 8;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, 8, 8);
      const data = ctx.getImageData(0, 0, 8, 8).data;
      let r = 0;
      let g = 0;
      let b = 0;
      for (let i = 0; i < data.length; i += 4) {
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
      }
      const n = data.length / 4;
      r /= n * 255;
      g /= n * 255;
      b /= n * 255;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const chroma = max - min;
      // A greyscale tile has no hue to borrow; keep the one already set.
      if (chroma < 0.02) return;
      let h;
      if (max === r) h = ((g - b) / chroma) % 6;
      else if (max === g) h = (b - r) / chroma + 2;
      else h = (r - g) / chroma + 4;
      h *= 60;
      if (h < 0) h += 360;
      // sRGB hue runs roughly 25 degrees behind OKLCH at these chromas.
      document.documentElement.style.setProperty('--hue', ((h + 25) % 360).toFixed(0));
    } catch {
      /* a tainted canvas just means the chrome keeps its current tint */
    }
  };
  img.src = src;
}

// The zoom ceiling follows the displayed image's real pixel size, so an
// upscaled background can be zoomed into its extra detail while a plain 1024
// render still cannot be blown up past what it has. Token guards a slow
// decode from applying a stale ceiling after another swap.
let ceilingToken = 0;
function probeTileCeiling(src) {
  const token = ++ceilingToken;
  const img = new Image();
  img.onload = () => {
    if (token === ceilingToken) setMaxTileSize(img.naturalWidth || 1024);
  };
  img.onerror = () => {
    if (token === ceilingToken) setMaxTileSize(1024);
  };
  img.src = src;
}

onBackgroundChange((src) => {
  railEl.classList.toggle('hidden', !src);
  if (src) {
    tintFromTile(src);
    probeTileCeiling(src);
  }
});

// ---------- creative upscale ----------
// Creative upscale uses the same Krea model and VAE the tessellation prompts
// were tuned against. At Krea's 10 default steps, a 0.70 guide leaves exactly
// 3 reconstruction steps; 0.75 would land between steps and be quantized.
const UPSCALE_MODEL = 'krea2_turbo_fp8_scaled';
const UPSCALE_MODEL_LABEL = 'Krea 2 Turbo';
const UPSCALE_SIZE = 1440;
const UPSCALE_SCALE_LABEL = '1.4×';
const UPSCALE_GUIDE_STRENGTH = 0.7;
const UPSCALE_DETAIL_CLAUSE =
  'Preserve the source identity, composition, shapes, colours, and seamless pattern structure. ' +
  'Fill the enlarged composition with dense, coherent, sharply resolved detail. Infer and ' +
  'synthesize the precise small structures and material-specific textures appropriate to only the ' +
  'subjects and medium actually visible. Render crisp contours, clearly separated small features, ' +
  'rich surface definition, and sharp local contrast throughout the image. Use the added image ' +
  'capacity for meaningful visual detail, never blur, empty softness, or random noise. Clean ISO 64 ' +
  'clarity with controlled low-noise tonal transitions.';
const UPSCALE_NEGATIVE_PROMPT =
  'blur, soft focus, muddy detail, grain, film grain, noise, speckling, pixelation, ' +
  'compression artifacts, indiscriminate smoothing, smeared detail, lost fine structure, ' +
  'oversharpening halos, crunchy texture, painterly fuzz, bloom';
const UPSCALE_FALLBACK_PROMPT = `seamless repeating pattern, rich colour. ${UPSCALE_DETAIL_CLAUSE}`;

const upscaleBtn = el('upscale-btn');
const upscaleToggle = el('upscale-toggle');
const upscaleKeep = el('upscale-keep');
const upscaleDownload = el('upscale-download');
const upscaleMarkup = upscaleBtn.innerHTML;
const UPSCALE_TITLE = upscaleBtn.title;
const UPSCALE_UNDO_MARKUP = '<span class="upscale-mark" aria-hidden="true">↺</span>';
const UPSCALE_UNDO_TITLE = 'Undo the upscale and return to the original';

// One held comparison: the tile as it was and its 2x repaint, so the toggle
// can flip between them. Any background arriving from outside the pair (a
// roll, a keeper, a stream pick) retires it.
let upscale = null; // { baseSrc, baseRecord, resultSrc, resultRecord }
let upscaleRun = null; // AbortController while a repaint is in flight

function setUpscaling(running, progress = null) {
  upscaleBtn.classList.toggle('busy', running);
  const percent = Number.isFinite(progress)
    ? Math.max(0, Math.min(100, Math.round(progress)))
    : null;
  upscaleBtn.title = running
    ? percent === null
      ? 'Preparing upscale… Click to stop'
      : `Upscaling: ${percent}% complete. Click to stop`
    : UPSCALE_TITLE;
  upscaleBtn.setAttribute(
    'aria-label',
    running
      ? percent === null
        ? 'Preparing upscale. Click to stop'
        : `Upscaling, ${percent}% complete. Click to stop`
      : 'Upscale tile to 1440 pixels with AI'
  );
  upscaleBtn.innerHTML = running
    ? percent === null
      ? '<span class="upscale-spinner" aria-hidden="true"></span>'
      : `<span class="upscale-progress">${percent}%</span>`
    : upscaleMarkup;
}

function updateUpscaleUI() {
  const src = currentBackground();
  upscaleBtn.classList.toggle('hidden', !(isAuthed && src));
  const showingResult = !!upscale && src === upscale.resultSrc;
  const inPair = !!upscale && (showingResult || src === upscale.baseSrc);
  upscaleToggle.classList.toggle('hidden', !inPair);
  upscaleToggle.setAttribute('aria-pressed', showingResult ? 'true' : 'false');
  upscaleToggle.title = showingResult ? 'Show the original' : 'Show the upscale';
  // While a pair is held the upscale button is an undo, never a second
  // application. A running repaint owns the button (progress percent), so
  // leave it alone until the run's own finally repaints.
  if (!upscaleRun) {
    upscaleBtn.innerHTML = inPair ? UPSCALE_UNDO_MARKUP : upscaleMarkup;
    upscaleBtn.title = inPair ? UPSCALE_UNDO_TITLE : UPSCALE_TITLE;
    upscaleBtn.setAttribute(
      'aria-label',
      inPair ? UPSCALE_UNDO_TITLE : 'Upscale tile to 1440 pixels with AI'
    );
  }
  // Keep and download ride the rail because that is where the upscale
  // happened: the winner card sits in the drawer, which rests closed and
  // never opens on its own, so an upscale finished against a closed drawer
  // used to offer no way to keep, publish, or save the result. They stay up
  // for the whole compare pair — always acting on the repaint — so flipping
  // the toggle never reflows the rail under the pointer.
  upscaleKeep.classList.toggle('hidden', !inPair);
  upscaleDownload.classList.toggle('hidden', !inPair);
  if (inPair) {
    const rec = upscale.resultRecord;
    const kept = !!rec.favorited;
    const title = rec.shared
      ? 'Kept & shared to the public stream'
      : kept
        ? 'Kept in your keepers'
        : 'Keep this upscale in your keepers';
    upscaleKeep.classList.toggle('kept', kept);
    upscaleKeep.title = title;
    upscaleKeep.setAttribute('aria-label', title);
  }
}

// First-time confirmation: what the upscale does, what it roughly costs, and
// a way to never be asked again. The flag persists only on an actual
// proceed — ticking the box and then cancelling should not silence a dialog
// the user never accepted.
const UPSCALE_ACK_KEY = 'sogni-infinity-upscale-ack';

async function fillUpscaleCost() {
  const costEl = el('upscale-cost');
  costEl.classList.add('hidden');
  const method = activePaymentMethod();
  if (isUnlimited && method === 'plan') {
    costEl.textContent = 'Included in your Unlimited plan.';
    costEl.classList.remove('hidden');
    return;
  }
  try {
    const token = paymentToken(method);
    const cost = await estimateRollCost({
      model: UPSCALE_MODEL,
      imageCount: 1,
      size: UPSCALE_SIZE,
      tokenType: token
    });
    const amount = cost?.[token];
    if (amount == null) return;
    const usd = cost.usd != null ? ` · ~$${cost.usd.toFixed(2)}` : '';
    costEl.innerHTML =
      token === 'spark'
        ? `Estimated cost: ${icon('bolt', 11)} ${Math.max(0, Math.round(amount)).toLocaleString()} Spark${usd}`
        : `Estimated cost: ${(Math.round(amount * 1000) / 1000).toLocaleString(undefined, { maximumFractionDigits: 3 })} SOGNI${usd}`;
    costEl.classList.remove('hidden');
  } catch {
    // The estimate is a nicety; the dialog stands without it.
  }
}

function confirmUpscale() {
  if (localStorage.getItem(UPSCALE_ACK_KEY) === '1') return Promise.resolve(true);
  const modal = el('upscale-modal');
  fillUpscaleCost(); // fills in while the dialog is already readable
  modal.classList.remove('hidden');
  return new Promise((resolve) => {
    const done = (go) => {
      modal.classList.add('hidden');
      if (go && el('upscale-dontask').checked) {
        localStorage.setItem(UPSCALE_ACK_KEY, '1');
      }
      resolve(go);
    };
    el('upscale-go').onclick = () => done(true);
    el('upscale-cancel').onclick = () => done(false);
    modal.onclick = (e) => {
      if (e.target === modal) done(false);
    };
  });
}

/** Native pixel width of an image URL, for the upscale-ceiling guard. */
function imageNativeSize(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img.naturalWidth || 0);
    img.onerror = () => reject(new Error('could not read the tile'));
    img.src = src;
  });
}

// The vision API takes an inline data URI capped at 1024px on the long side,
// so the tile is re-encoded to JPEG from the blob. Only the LLM sees this
// copy — the render itself gets the untouched original bytes.
async function visionDataUrl(blob) {
  const bitmap = await createImageBitmap(blob);
  const long = Math.max(bitmap.width, bitmap.height);
  const scale = Math.min(1, 1024 / long);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL('image/jpeg', 0.85);
}

upscaleBtn.onclick = async () => {
  if (upscaleRun) {
    upscaleRun.abort();
    return;
  }
  if (!isAuthed) return;

  // One application only: while a pair is held, the button is an undo.
  // Upscaling the upscale would compound past this feature's ceiling, and
  // the old press-again-to-re-roll proved too easy to trigger by accident —
  // undo, then upscale again, if you want another roll of the dice.
  if (upscale) {
    const { baseSrc: undoSrc, baseRecord: undoRecord, resultSrc, resultRecord } = upscale;
    upscale = null;
    current = undoRecord;
    setBackground(undoSrc);
    if (undoRecord) {
      winnerEl.classList.remove('hidden');
      winnerLabel.textContent = `${undoRecord.emoji} ${undoRecord.label}`;
      setKeepButton(undoRecord.shared ? 'shared' : undoRecord.favorited ? 'saved' : 'keep');
    } else {
      winnerEl.classList.add('hidden');
    }
    // The repaint's blob goes with it once the crossfade is done, unless it
    // was kept — the keeper shelf owns its own copy by then.
    if (!resultRecord.favorited) {
      setTimeout(() => URL.revokeObjectURL(resultSrc), 2000);
    }
    statusEl.textContent = 'Upscale undone.';
    updateUpscaleUI();
    return;
  }

  const baseSrc = currentBackground();
  if (!baseSrc) return;
  const baseRecord = current && current.src === baseSrc ? current : null;

  // The ceiling guard runs before the confirm dialog: asking someone to
  // approve a job that could never start would read as a broken promise.
  let nativePx = 0;
  try {
    nativePx = await imageNativeSize(baseSrc);
  } catch {
    // An unreadable tile: let the render path surface the real error.
  }
  if (nativePx >= UPSCALE_SIZE) {
    statusEl.textContent = `This tile is already ${nativePx}px — the creative-upscale ceiling.`;
    return;
  }
  if (!(await confirmUpscale())) return;

  // Which keeper record and stream entry this image already occupies, so a
  // kept upscale overwrites its original instead of standing next to it.
  // baseMeta carries the original's name when the base is not `current` —
  // a shelf pick or one of the user's own wall tiles.
  let keeperId = baseRecord?.favorited ? baseRecord.id : null;
  let baseMeta = null;
  let baseStreamId = baseRecord?.streamId || null;
  try {
    if (!keeperId) {
      for (const [id, url] of objectUrls) {
        if (url === baseSrc) {
          keeperId = id;
          break;
        }
      }
      if (keeperId) baseMeta = favIndex().find((f) => f.id === keeperId) || null;
    }
    if (!baseStreamId && keeperId) {
      baseStreamId = (await loadFavorite(keeperId))?.streamId || null;
    }
    if (!baseStreamId) {
      const own = streamItems.find(
        (it) => it.url === baseSrc && it.author && it.author === currentUsername
      );
      if (own) {
        baseStreamId = own.id;
        baseMeta = baseMeta || { label: own.subject, emoji: '✨' };
      }
    }
  } catch {
    // Identity is a nicety: without it a keep still works as a fresh save.
  }

  const run = new AbortController();
  run.base = baseSrc;
  upscaleRun = run;
  setUpscaling(true);
  try {
    const blob = await fetch(baseSrc).then((r) => r.blob());
    statusEl.textContent = 'Studying the tile…';
    const sourcePrompt = baseRecord?.prompt?.trim() || '';
    let visualPrompt = '';
    try {
      visualPrompt = await describeTileForUpscale({
        dataUrl: await visionDataUrl(blob),
        sourcePrompt: sourcePrompt || null,
        allowMature: allowMature()
      });
    } catch {
      // The LLM is an enhancer, not a dependency: fall back to what we know.
    }
    if (run.signal.aborted) return;
    // A stored dynamic batch prompt contains several possible subjects.
    // Reusing it directly could choose a branch unrelated to the actual tile;
    // the image-grounded vision rewrite is authoritative in that case.
    const sourceIsDynamic = /\{[^{}]*\|[^{}]*\}/.test(sourcePrompt);
    let prompt = [sourceIsDynamic ? '' : sourcePrompt, visualPrompt].filter(Boolean).join('. ');
    if (!prompt) prompt = sourcePrompt || UPSCALE_FALLBACK_PROMPT;
    if (!prompt.includes(UPSCALE_DETAIL_CLAUSE)) {
      prompt = `${prompt.replace(/\s+$/, '').replace(/[.,;:]$/, '')}. ${UPSCALE_DETAIL_CLAUSE}`;
    }

    statusEl.textContent = `Repainting at ${UPSCALE_SIZE}px with ${UPSCALE_MODEL_LABEL}…`;
    const seed = randomSeed();
    const modelCfg = (config.models || []).find((m) => m.id === UPSCALE_MODEL);
    const url = await renderUpscale({
      model: UPSCALE_MODEL,
      vae: modelCfg?.vae || config.vae,
      prompt,
      image: blob,
      strength: UPSCALE_GUIDE_STRENGTH,
      seed,
      size: UPSCALE_SIZE,
      negativePrompt: UPSCALE_NEGATIVE_PROMPT,
      outputFormat: 'png',
      allowMature: allowMature(),
      signal: run.signal,
      onProgress: (percent) => {
        if (upscaleRun !== run || run.signal.aborted) return;
        setUpscaling(true, percent);
        statusEl.textContent = `Upscaling at ${UPSCALE_SIZE}px… ${Math.round(percent)}%`;
      }
    });

    // Score for feedback only: the wrap can drift under 25% denoise, and the
    // user deserves to know before they keep it. Publishing re-scores anyway.
    let verdict = null;
    try {
      verdict = (await scoreUrl(url, run.signal)).verdict;
    } catch {
      /* the badge is optional */
    }
    const resultBlob = await fetch(url).then((r) => r.blob());
    if (run.signal.aborted) return;
    // The user may have moved to another background mid-flight; do not swap a
    // repaint in over a tile it does not belong to.
    if (currentBackground() !== baseSrc) return;

    const resultSrc = URL.createObjectURL(resultBlob);
    const resultRecord = {
      // Descending from a keeper means inheriting its id: the save in
      // keepCurrent then overwrites that record rather than duplicating it.
      id: keeperId || `fav-${Date.now()}`,
      blob: resultBlob,
      src: resultSrc,
      label: baseRecord
        ? `${baseRecord.label} · ${UPSCALE_SCALE_LABEL}`
        : baseMeta
          ? `${baseMeta.label} · ${UPSCALE_SCALE_LABEL}`
          : `Upscaled tile · ${UPSCALE_SCALE_LABEL}`,
      emoji: baseRecord?.emoji || baseMeta?.emoji || '✨',
      seed,
      prompt,
      model: UPSCALE_MODEL,
      size: UPSCALE_SIZE,
      remoteUrl: url,
      streamId: baseStreamId,
      favorited: false
    };
    upscale = { baseSrc, baseRecord, resultSrc, resultRecord };
    current = resultRecord;
    setBackground(resultSrc);
    enterWorkingPhase();
    // The winner card lives inside #results, which only a roll reveals — an
    // upscale of a keeper or stream pick in a fresh session would otherwise
    // paint a Keep card into a container that is still display:none.
    el('results').classList.remove('hidden');
    winnerEl.classList.remove('hidden');
    winnerLabel.textContent = `${resultRecord.emoji} ${resultRecord.label}`;
    setKeepButton('keep');
    statusEl.textContent =
      verdict === 'pass'
        ? 'Upscaled, and the wrap still checks seamless. Flip the compare toggle to judge it.'
        : verdict
          ? `Upscaled — the seam check says "${verdict}". Compare before keeping.`
          : 'Upscaled. Flip the compare toggle to judge it against the original.';
  } catch (err) {
    // A cancelled project can surface as its own rejection rather than an
    // AbortError, so the signal is the authority on "the user stopped this".
    if (run.signal.aborted || err?.name === 'AbortError') {
      statusEl.textContent = 'Upscale stopped.';
    } else if (isInsufficientFunds(err)) {
      statusEl.textContent = 'Not enough Spark for the upscale.';
      showUpsell();
    } else {
      statusEl.textContent = String(err.message || err).slice(0, 120);
    }
  } finally {
    if (upscaleRun === run) upscaleRun = null;
    setUpscaling(false);
    updateUpscaleUI();
  }
};

// Both act on the repaint whichever side of the compare is showing.
upscaleKeep.onclick = () => {
  if (upscale) keepCurrent(upscale.resultRecord);
};

upscaleDownload.onclick = () => {
  if (!upscale) return;
  const rec = upscale.resultRecord;
  downloadTile(upscale.resultSrc, rec.label, rec.seed);
};

upscaleToggle.onclick = () => {
  if (!upscale) return;
  const showingResult = currentBackground() === upscale.resultSrc;
  const next = showingResult
    ? { src: upscale.baseSrc, record: upscale.baseRecord }
    : { src: upscale.resultSrc, record: upscale.resultRecord };
  current = next.record;
  setBackground(next.src);
  if (next.record) {
    winnerEl.classList.remove('hidden');
    winnerLabel.textContent = `${next.record.emoji} ${next.record.label}`;
    setKeepButton(next.record.shared ? 'shared' : next.record.favorited ? 'saved' : 'keep');
  } else {
    // The original came from a collection, not a roll — there is no record to
    // keep, so the card goes rather than offering a Keep that would not work.
    winnerEl.classList.add('hidden');
  }
};

onBackgroundChange((src) => {
  // A background from outside the held pair retires the comparison and stops
  // any repaint still in flight for it.
  if (upscale && src && src !== upscale.baseSrc && src !== upscale.resultSrc) {
    upscale = null;
  }
  if (upscaleRun && src && src !== upscaleRun.base && (!upscale || src !== upscale.resultSrc)) {
    upscaleRun.abort();
  }
  updateUpscaleUI();
});

// ---------- animate (seamless video loop) ----------
// The tile becomes both anchor frames of a MiniMax H3 first/last-frame video
// render, so the ~5s clip starts and ends on the artwork — a closed loop that
// keeps tiling while it moves (feasibility measured in
// research/video-loop.mjs before this shipped). The result plays as the
// live background via videowall.js; it is deliberately a quiet corner
// feature, one dock beside the upscale.

const animateBtn = el('animate-btn');
const animateToggle = el('animate-toggle');
const animateMute = el('animate-mute');
const animateDownload = el('animate-download');
const animateMarkup = animateBtn.innerHTML;
const ANIMATE_TITLE = animateBtn.title;
const ANIMATE_ACK_KEY = 'sogni-infinity-animate-ack';

// Which engine renders the loop (see ANIMATION_ENGINES). LTX 2.3 is the
// default: it animates the tile at its NATIVE 1024 resolution and measures
// roughly a tenth of H3's price. The 20-step H3 is the platform-gated finest
// tier (the renderer falls back to Turbo when an account cannot use it). The
// choice persists like the payment method does.
const ANIMATE_MODEL_KEY = 'sogni-infinity-animate-model';

function animateModelPref() {
  let stored = null;
  try {
    stored = localStorage.getItem(ANIMATE_MODEL_KEY);
  } catch {
    return 'ltx';
  }
  // The first shipped build stored the H3-only vocabulary.
  if (stored === 'turbo') return 'h3-turbo';
  if (stored === 'standard') return 'h3';
  return ANIMATION_ENGINES[stored] ? stored : 'ltx';
}

function saveAnimateModelPref(value) {
  try {
    localStorage.setItem(ANIMATE_MODEL_KEY, ANIMATION_ENGINES[value] ? value : 'ltx');
  } catch {
    /* private mode: the choice just does not persist across reloads */
  }
}

// Loop length, persisted like the model. The choices are labels; each engine
// snaps to its own frame grid (H3's "10s" really renders 10.13s) and the H3
// prompt states the snapped value. The motion suggestion is deliberately NOT
// persisted: a hint written for koi would silently misdirect next week's
// skulls, so it lives only as the input's value for the session.
//
// Only the lengths that measured reliable are offered. Per-frame seam scoring
// of the same tile/seed across every second from 5-10 plus 15
// (research/video-loop.mjs, 2026-08-09): 5s, 6s, and 7s each held a 100%
// spatial-seam pass rate; 8s dropped to 87.6% with the worst mismatch of the
// sweep, 9s to 98.6%, and 15s to 98.3%. 10s measured 100% but a ten-second
// loop overstays as wallpaper, so the list stops where reliability is
// uniform. Loop closure held at every length — the anchors work — so longer
// cuts can return if they remeasure clean across seeds.
const ANIMATE_DURATION_KEY = 'sogni-infinity-animate-duration';
const ANIMATE_DURATIONS = [5, 6, 7];

function animateDurationPref() {
  try {
    const stored = Number(localStorage.getItem(ANIMATE_DURATION_KEY));
    return ANIMATE_DURATIONS.includes(stored) ? stored : 5;
  } catch {
    return 5;
  }
}

function saveAnimateDurationPref(value) {
  try {
    const seconds = Number(value);
    if (ANIMATE_DURATIONS.includes(seconds)) {
      localStorage.setItem(ANIMATE_DURATION_KEY, String(seconds));
    }
  } catch {
    /* private mode: the choice just does not persist across reloads */
  }
}

// The visitor's sound choice for this session. It starts unmuted — the loop
// ships with H3's own soundtrack — but the browser may refuse un-muted
// autoplay when the render lands minutes after the click that ordered it, so
// the UI always re-reads the wall's real state after starting playback.
let wallMuted = false;

// One held loop: the tile it was made from and its mp4, so the toggle can
// flip between the still and the motion. A background from outside the pair
// retires it, exactly like the upscale compare.
let animation = null; // { baseSrc, videoSrc, blob, label, seed }
let animationRun = null; // AbortController while a render is in flight

function setAnimating(running, progress = null) {
  animateBtn.classList.toggle('busy', running);
  const percent = Number.isFinite(progress)
    ? Math.max(0, Math.min(100, Math.round(progress)))
    : null;
  animateBtn.title = running
    ? percent === null
      ? 'Preparing the loop… Click to stop'
      : `Animating: ${percent}% complete. Click to stop`
    : ANIMATE_TITLE;
  animateBtn.setAttribute(
    'aria-label',
    running
      ? percent === null
        ? 'Preparing the loop. Click to stop'
        : `Animating, ${percent}% complete. Click to stop`
      : 'Animate this tile into a seamless loop'
  );
  animateBtn.innerHTML = running
    ? percent === null
      ? '<span class="upscale-spinner" aria-hidden="true"></span>'
      : `<span class="upscale-progress">${percent}%</span>`
    : animateMarkup;
}

function updateAnimateUI() {
  const src = currentBackground();
  const holdsLoop = !!animation && src === animation.baseSrc;
  // While a loop is held, its transport controls take the dock over; the
  // render button would only duplicate the play toggle beside it.
  animateBtn.classList.toggle('hidden', !(isAuthed && src) || holdsLoop);
  animateToggle.classList.toggle('hidden', !holdsLoop);
  animateMute.classList.toggle('hidden', !holdsLoop);
  animateDownload.classList.toggle('hidden', !holdsLoop);
  if (!holdsLoop) return;

  const playing = isVideoWallActive();
  animateToggle.innerHTML = icon(playing ? 'pause' : 'play', 16);
  animateToggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
  const toggleTitle = playing ? 'Pause the loop and show the still tile' : 'Play the loop';
  animateToggle.title = toggleTitle;
  animateToggle.setAttribute('aria-label', toggleTitle);

  // The sound button reports the wall's REAL state, which can differ from the
  // visitor's choice when un-muted autoplay was refused.
  const muted = isVideoWallMuted();
  animateMute.innerHTML = icon(muted ? 'muted' : 'sound', 16);
  animateMute.setAttribute('aria-pressed', muted ? 'true' : 'false');
  const muteTitle = muted ? "Unmute the loop's soundtrack" : "Mute the loop's soundtrack";
  animateMute.title = muteTitle;
  animateMute.setAttribute('aria-label', muteTitle);
  animateMute.disabled = !playing;
}

async function fillAnimateCost() {
  const costEl = el('animate-cost');
  costEl.classList.add('hidden');
  const method = activePaymentMethod();
  if (isUnlimited && method === 'plan') {
    costEl.textContent = 'Included in your Unlimited plan.';
    costEl.classList.remove('hidden');
    return;
  }
  try {
    const token = paymentToken(method);
    const cost = await estimateAnimationCost(token, animateModelPref(), animateDurationPref());
    const amount = cost?.[token];
    if (amount == null) return;
    const usd = cost.usd != null ? ` · ~$${cost.usd.toFixed(2)}` : '';
    costEl.innerHTML =
      token === 'spark'
        ? `Estimated cost: ${icon('bolt', 11)} ${Math.max(0, Math.round(amount)).toLocaleString()} Spark${usd}`
        : `Estimated cost: ${(Math.round(amount * 1000) / 1000).toLocaleString(undefined, { maximumFractionDigits: 3 })} SOGNI${usd}`;
    costEl.classList.remove('hidden');
  } catch {
    // The estimate is a nicety; the dialog stands without it.
  }
}

function confirmAnimate() {
  if (localStorage.getItem(ANIMATE_ACK_KEY) === '1') return Promise.resolve(true);
  const modal = el('animate-modal');
  fillAnimateCost(); // fills in while the dialog is already readable
  // The model and length pickers mirror the saved choices and save on
  // selection — they are settings, not consents, so they stick even if this
  // dialog is cancelled. Both change the price, so either refreshes the
  // estimate.
  for (const radio of modal.querySelectorAll('input[name="animate-model"]')) {
    radio.checked = radio.value === animateModelPref();
    radio.onchange = () => {
      if (!radio.checked) return;
      saveAnimateModelPref(radio.value);
      fillAnimateCost();
    };
  }
  for (const radio of modal.querySelectorAll('input[name="animate-duration"]')) {
    radio.checked = Number(radio.value) === animateDurationPref();
    radio.onchange = () => {
      if (!radio.checked) return;
      saveAnimateDurationPref(radio.value);
      fillAnimateCost();
    };
  }
  // The example wall only downloads once someone actually opens this dialog,
  // and only plays while it is up.
  const demo = el('animate-demo');
  if (demo && !demo.src) demo.src = demo.dataset.src;
  demo?.play().catch(() => {});
  modal.classList.remove('hidden');
  return new Promise((resolve) => {
    const done = (go) => {
      modal.classList.add('hidden');
      demo?.pause();
      if (go && el('animate-dontask').checked) {
        localStorage.setItem(ANIMATE_ACK_KEY, '1');
      }
      resolve(go);
    };
    el('animate-go').onclick = () => done(true);
    el('animate-cancel').onclick = () => done(false);
    modal.onclick = (e) => {
      if (e.target === modal) done(false);
    };
  });
}

/** The anchor frame: the tile's own image-model output, scaled to the chosen
 *  engine's canvas (a no-op for LTX's native 1024). Never a frame extracted
 *  from a video. */
async function animationAnchor(blob, size) {
  const bitmap = await createImageBitmap(blob);
  if (bitmap.width === size && bitmap.height === size) {
    bitmap.close();
    return blob;
  }
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, size, size);
  bitmap.close();
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (out) => (out ? resolve(out) : reject(new Error('could not prepare the tile'))),
      'image/png'
    );
  });
}

function retireAnimation() {
  if (!animation) return;
  hideVideoWall();
  const gone = animation;
  animation = null;
  // Give the wall's fade time to finish before its frames' source vanishes.
  setTimeout(() => URL.revokeObjectURL(gone.videoSrc), 2000);
}

animateBtn.onclick = async () => {
  if (animationRun) {
    animationRun.abort();
    return;
  }
  if (!isAuthed) return;
  const baseSrc = currentBackground();
  if (!baseSrc) return;

  // Pressing play on the tile that already holds a paused loop resumes it
  // rather than paying for a second render of the same thing.
  if (animation && animation.baseSrc === baseSrc) {
    await showVideoWall(animation.videoSrc, { muted: wallMuted });
    updateAnimateUI();
    return;
  }

  if (!(await confirmAnimate())) return;
  retireAnimation();

  const baseRecord = current && current.src === baseSrc ? current : null;
  const label = baseRecord?.label || 'tile';
  const run = new AbortController();
  run.base = baseSrc;
  animationRun = run;
  setAnimating(true);
  try {
    const engine = animateModelPref();
    const engineCfg = ANIMATION_ENGINES[engine];
    const seconds = animateDurationPref();
    const motionHint = el('animate-suggestion')?.value.trim() || undefined;
    const blob = await fetch(baseSrc).then((r) => r.blob());
    const anchor = await animationAnchor(blob, engineCfg.size);
    if (run.signal.aborted) return;
    statusEl.textContent = `Animating a ${seconds}s seamless loop with ${engineCfg.label}…`;
    const { url } = await renderTileAnimation({
      image: anchor,
      subjectLabel: baseRecord?.label,
      // The visitor's saved choices. The 20-step H3 is gated to Unlimited /
      // Premium Spark / SOGNI billing; the renderer falls back to Turbo on
      // the gate error, so a Spark account still gets its loop.
      engine,
      seconds,
      motionHint,
      seed: randomSeed(),
      signal: run.signal,
      onProgress: (percent) => {
        if (animationRun !== run || run.signal.aborted) return;
        setAnimating(true, percent);
        statusEl.textContent = `Animating the loop… ${Math.round(percent)}%`;
      }
    });
    const videoBlob = await fetch(url).then((r) => r.blob());
    if (run.signal.aborted) return;
    // The user may have moved on mid-flight; a loop must not play over a
    // tile it does not belong to.
    if (currentBackground() !== baseSrc) return;

    animation = {
      baseSrc,
      videoSrc: URL.createObjectURL(videoBlob),
      blob: videoBlob,
      label: `${label} loop`,
      seed: baseRecord?.seed
    };
    await showVideoWall(animation.videoSrc, { muted: wallMuted });
    statusEl.textContent =
      isVideoWallMuted() && !wallMuted
        ? 'The tile is alive. The browser held its soundtrack back — tap the sound button to hear it.'
        : 'The tile is alive — it loops seamlessly and keeps tiling while it moves.';
  } catch (err) {
    if (run.signal.aborted || err?.name === 'AbortError') {
      statusEl.textContent = 'Animation stopped.';
    } else if (isInsufficientFunds(err)) {
      statusEl.textContent = 'Not enough Spark for the animation.';
      showUpsell();
    } else {
      statusEl.textContent = String(err.message || err).slice(0, 120);
    }
  } finally {
    if (animationRun === run) animationRun = null;
    setAnimating(false);
    updateAnimateUI();
  }
};

animateToggle.onclick = async () => {
  if (!animation) return;
  if (isVideoWallActive()) {
    hideVideoWall();
  } else {
    await showVideoWall(animation.videoSrc, { muted: wallMuted });
  }
  updateAnimateUI();
};

animateMute.onclick = () => {
  if (!animation) return;
  wallMuted = !isVideoWallMuted();
  setVideoWallMuted(wallMuted);
  updateAnimateUI();
};

animateDownload.onclick = () => {
  if (!animation) return;
  downloadTile(animation.videoSrc, animation.label, animation.seed);
};

onBackgroundChange((src) => {
  // A background from outside the held loop retires it and stops any render
  // still in flight for a tile that is no longer on screen.
  if (animation && src && src !== animation.baseSrc) {
    retireAnimation();
  }
  if (animationRun && src && src !== animationRun.base) {
    animationRun.abort();
  }
  updateAnimateUI();
});

// ---------- prompt inspector ----------
el('prompt-btn').onclick = () => {
  if (current) {
    openPromptModal({ ...current, name: `${current.emoji} ${current.label}` });
  }
};

// "Render this prompt" from the inspector: the edited text becomes the one
// prompt for every slot of a fresh roll. Subject id 'custom' keeps
// promptsForBatch from swapping in random styles over the visitor's wording.
onPromptResubmit(async (prompt, entry) => {
  if (!isAuthed) {
    statusEl.textContent = 'Sign in with your Sogni account to render your own tiles.';
    return;
  }
  if (activeRoll) return; // a roll is already running
  // A tile born in photo mode carries a prompt that references "the reference
  // photo" — rendering it without a context image would invent a stranger.
  // Rerolling it keeps the identity model and needs a photo loaded; the one
  // it was born from is never stored, so any currently-loaded photo is used.
  const photoTile = !!config.photo && entry.model === config.photo.model;
  if (photoTile && !photoBlob) {
    statusEl.textContent =
      'This prompt needs a photo. Pick 📷 Your photo, choose one, then render again.';
    return;
  }
  exitPreview();
  winnerEl.classList.add('hidden');
  await startRoll({
    id: 'custom',
    // photoStyle picks the donor. Tiles kept before styles were per-donor
    // carry none; photoDonor falls back rather than refusing the roll.
    ...(photoTile ? { photo: true, photoStyle: entry.photoStyle } : {}),
    // A tile entry carries its own emoji and plain label; preview items fold
    // the emoji into `name` instead, so fall back through that.
    label: entry.emoji ? entry.label : entry.name || entry.label || 'custom prompt',
    emoji: entry.emoji || '✨',
    prompt
  });
});

// ---------- boot ----------

/** Open on a random tile from the public stream. The background stays at its
 *  dark base colour until the selected image has decoded, so no unrelated
 *  bundled tile flashes before the real opening state is ready. */
async function randomStreamBackground() {
  try {
    const { entries } = await fetchStream({ sort: 'new', limit: 60 });
    if (!entries.length) return;
    const pick = entries[Math.floor(Math.random() * entries.length)];
    // Decode first: swapping to a URL still in flight paints an empty page
    // for as long as the fetch takes.
    await new Promise((resolve, reject) => {
      const probe = new Image();
      probe.onload = resolve;
      probe.onerror = reject;
      probe.src = pick.url;
    });
    // The user may have generated something while this was loading, and their
    // tile outranks a decorative one.
    if (currentBackground()) return;
    // Immediate, not a crossfade: this is the page arriving at its opening
    // state, and fading in from the bundled tile advertised the placeholder as
    // if it were a choice.
    setBackground(pick.url, { immediate: true });
  } catch {
    /* keep the dark base colour */
  }
}

(async () => {
  // Kicked off before anything else and never awaited so the real backdrop
  // arrives as early as the network and image decode allow.
  randomStreamBackground();

  const [cfg, cat] = await Promise.all([
    fetch('/api/config').then((r) => r.json()),
    fetch('/api/subjects').then((r) => r.json())
  ]);
  config = cfg;
  subjects = cat.subjects;
  styles = cat.styles;
  setPromptConfig(config);
  setReportConfig(config);
  initSettings(config);
  initSparkModal({
    estimateCost: async () => (await estimateCurrentRollCost('spark'))?.spark
  });
  initMonthlyBoost({
    turnstileSiteKey: config.turnstileSiteKey,
    claimed: async () => {
      const billing = await refreshAccountChip(currentUsername);
      await refreshMonthlyBoost(billing.subscription?.status);
      statusEl.textContent = 'Monthly Boost claimed — your free Spark is ready.';
    }
  });
  initAuthModal({
    authenticated: initAuth,
    turnstileSiteKey: config.turnstileSiteKey
  });
  populatePicker();
  el('unlimited-link').href = config.upsellUrl;
  el('upsell-link').href = config.upsellUrl;
  await initAuth();
  renderSamples();
  renderShelf();
  startUpdateWatch();
})();
